import React, { useState, useEffect, useMemo, useCallback } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import DataTable from "react-data-table-component";

// API
import ItemService from "../../../../services/customer_appCommonService";

// Components
import Layout from "../../../../Layout/Layout";
import { InputField } from "../../../../Components/formElements/InputField";
import { TextAreaField } from "../../../../Components/formElements/TextAreaField";
import { ComboboxField } from "../../../../Components/formElements/ComboboxField";
// import { ERFComboboxField } from "./ERFComboboxField";
import Loader from "../../../../Components/Loader";
import MaterialSelectionModalV5 from "./MaterialSearchModal";
import FailureModal from "../../../../Components/Modal/supplier_app/FailureModal";
import Constants from "../../../../common/CustomerConstants";
import GlobalRentConfigPanel from "./GlobalRentConfigPanel";

// Validations
import {
  getTodaysData,
  validateRequired,
  validateContactNumber,
  validateAadhar,
  validatePinCode,
  validatePAN,
} from "../../../../common/Validations";

// Constants & Utils
import {
  ERF_TYPES,
  PRIVILEGE_SETTINGS,
  DEFAULT_PRIVILEGE_SETTINGS,
  PAYMENT_MODES,
  PAYMENT_MODE_RULES,
} from "./ERFConfigFileConstant";

import {
  SALE_POINT_OPTIONS,
  ORDER_TYPE_OPTIONS,
  SOURCE_OF_CALL_OPTIONS,
  ID_PROOF_OPTIONS,
  DOCTOR_TYPE_OPTIONS,
  STATE_OPTIONS,
  IS_DIABETIC_OPTIONS,
  APPROVED_BY_OPTIONS_LEVEL_ONE,
  PATIENT_TYPE_OPTIONS,
  GENDER_OPTIONS,
  DOCTOR_TYPE_SELECTION_OPTIONS,
  // COST_POINT_OPTIONS,
} from "./constants";

// eslint-disable-next-line
import { message, calculateDays, calculateEndDate } from "./utils";
// import ERFAutocompleteSearch from "./ERFAutocompleteSearch";
import { getEquipmentRentFormList } from "../../../../redux/feature/customer_app/customerSlice";
// import TableDropdownMenu from "../../../../Components/TableDropdownMenu/TableDropdownMenu";

// ----------Approval Process-----------------
const APPROVAL_REQUIRED_CASES = [
  "SPECIAL CASE",
  "DISCOUNT",
  "SPECIAL CASE+DISCOUNT",
];

// 2. customStyles outside
const CONDITIONAL_ROW_STYLES = [
  {
    when: (row) => !!row.isTotal,
    style: {
      backgroundColor: "#cfe2ff",
      color: "#084298",
      fontWeight: "bold",
    },
  },
];

const detectPriceViolation = (item, itemType) => {
  const violations = [];

  // ── SHARED RENT + NURSING LOGIC ──────────────────────────────────────────
  if (itemType === "Rent" || itemType === "Nursing") {
    const netValue = parseFloat(item.netValue) || 0;
    const days = parseFloat(item.days) || 1;
    const qty = parseFloat(item.orderedQuantity) || 1;

    if (days <= 0 || qty <= 0) return [];

    // Effective net rate/day the user has set
    const currentNetRatePerDay = netValue / (days * qty);

    // Master net rate/day for the selected slab
    let masterNetRatePerDay = 0;
    switch (item.rentSlab) {
      case "Per Day":
        masterNetRatePerDay = parseFloat(item.rentPerDay) || 0;
        break;
      case "7 Days":
        masterNetRatePerDay = (parseFloat(item.rent7Days) || 0) / 7;
        break;
      case "15 Days":
        masterNetRatePerDay = (parseFloat(item.rent15Days) || 0) / 15;
        break;
      case "30 Days":
        masterNetRatePerDay = (parseFloat(item.rent30Days) || 0) / 30;
        break;
      default:
        masterNetRatePerDay = parseFloat(item.rentPerDay) || 0;
    }

    if (Math.abs(currentNetRatePerDay - masterNetRatePerDay) > 0.01) {
      violations.push({
        itemId: item.local_id,
        itemCode: item.materialCode,
        itemDescription: item.materialDescription,
        itemType,
        violationType: "Rate Change",
        masterRate: masterNetRatePerDay.toFixed(2),
        changedRate: currentNetRatePerDay.toFixed(2),
        message: `${itemType} net rate changed from ₹${masterNetRatePerDay.toFixed(2)}/day to ₹${currentNetRatePerDay.toFixed(2)}/day (${item.rentSlab})`,
      });
    }
  }

  // ── SALE LOGIC ───────────────────────────────────────────────────────────
  else if (itemType === "Sale") {
    const netValue = parseFloat(item.netValue) || 0;
    const masterMRP = parseFloat(item.masterMRP) || 0; // net
    const masterSellingPrice = parseFloat(item.masterSellingPrice) || 0; // net
    const qty = parseFloat(item.orderedQuantity) || 1;
    const netPerUnit = netValue / qty;

    if (netPerUnit > masterMRP + 0.01) {
      violations.push({
        itemId: item.local_id,
        itemCode: item.materialCode,
        itemDescription: item.materialDescription,
        itemType: "Sale",
        violationType: "Above MRP",
        masterMRP: masterMRP.toFixed(2),
        changedNetUnit: netPerUnit.toFixed(2),
        message: `Net unit ₹${netPerUnit.toFixed(2)} exceeds Master MRP ₹${masterMRP.toFixed(2)}`,
      });
    }

    if (netPerUnit < masterSellingPrice - 0.01) {
      violations.push({
        itemId: item.local_id,
        itemCode: item.materialCode,
        itemDescription: item.materialDescription,
        itemType: "Sale",
        violationType: "Below Selling Price",
        masterSellingPrice: masterSellingPrice.toFixed(2),
        changedNetUnit: netPerUnit.toFixed(2),
        message: `Net unit ₹${netPerUnit.toFixed(2)} below Master ₹${masterSellingPrice.toFixed(2)}`,
      });
    }
  }

  // ── SERVICE LOGIC ────────────────────────────────────────────────────────
  else if (itemType === "Service") {
    const netValue = parseFloat(item.netValue) || 0;
    const masterMRP = parseFloat(item.masterMRP) || 0;
    const masterSellingPrice = parseFloat(item.masterSellingPrice) || 0;
    const qty = parseFloat(item.orderedQuantity) || 1;
    const netPerUnit = netValue / qty;

    if (netPerUnit > masterMRP + 0.01) {
      violations.push({
        itemId: item.local_id,
        itemCode: item.materialCode,
        itemDescription: item.materialDescription,
        itemType: "Service",
        violationType: "Above MRP",
        masterMRP: masterMRP.toFixed(2),
        changedNetUnit: netPerUnit.toFixed(2),
        message: `Service net ₹${netPerUnit.toFixed(2)} exceeds MRP ₹${masterMRP.toFixed(2)}`,
      });
    }

    if (netPerUnit < masterSellingPrice - 0.01) {
      violations.push({
        itemId: item.local_id,
        itemCode: item.materialCode,
        itemDescription: item.materialDescription,
        itemType: "Service",
        violationType: "Below Service Price",
        masterSellingPrice: masterSellingPrice.toFixed(2),
        changedNetUnit: netPerUnit.toFixed(2),
        message: `Service net ₹${netPerUnit.toFixed(2)} below Master ₹${masterSellingPrice.toFixed(2)}`,
      });
    }
  }

  return violations;
};

const calculateRentTotals = (item) => {
  const qty = parseFloat(item.orderedQuantity) || 0;
  const days = parseFloat(item.days) || 0;
  const tax = parseFloat(item.taxPercentage) || 0;

  let masterNetRateForSlab = 0; // net value for one unit over the entire period

  switch (item.rentSlab) {
    case "Per Day":
      // rentPerDay is the net rate for one day per unit
      masterNetRateForSlab = (parseFloat(item.rentPerDay) || 0) * days;
      break;
    case "7 Days":
      // rent7Days is the net lump amount for 7 days per unit
      masterNetRateForSlab = parseFloat(item.rent7Days) || 0;
      break;
    case "15 Days":
      masterNetRateForSlab = parseFloat(item.rent15Days) || 0;
      break;
    case "30 Days":
      masterNetRateForSlab = parseFloat(item.rent30Days) || 0;
      break;
    default:
      masterNetRateForSlab = (parseFloat(item.rentPerDay) || 0) * days;
  }

  const netVal = qty * masterNetRateForSlab; // Net Value
  const taxAmt = netVal * (tax / 100); // Tax Amount
  const grossVal = netVal + taxAmt; // Gross Value

  // Rate/Day shown in the Rate/Day column — always the net rate per day per unit
  let ratePerDay = 0;
  if (item.rentSlab === "Per Day") {
    ratePerDay = parseFloat(item.rentPerDay) || 0;
  } else {
    // For fixed slabs, express as effective net rate/day
    ratePerDay = days > 0 ? masterNetRateForSlab / days : 0;
  }

  return {
    netValue: parseFloat(netVal.toFixed(2)),
    taxAmount: parseFloat(taxAmt.toFixed(2)),
    grossValue: parseFloat(grossVal.toFixed(2)),
    sellingPrice: parseFloat(ratePerDay.toFixed(4)), // net rate/day
  };
};

const calculateSaleTotals = (item) => {
  const qty = parseFloat(item.orderedQuantity) || 0;
  const netUnitPrice = parseFloat(item.sellingPrice) || 0; // net unit price
  const tax = parseFloat(item.taxPercentage) || 0;

  const netVal = qty * netUnitPrice;
  const taxAmt = netVal * (tax / 100);
  const grossVal = netVal + taxAmt;

  return {
    netValue: parseFloat(netVal.toFixed(2)),
    taxAmount: parseFloat(taxAmt.toFixed(2)),
    grossValue: parseFloat(grossVal.toFixed(2)),
    sellingPrice: parseFloat(netUnitPrice.toFixed(2)),
  };
};

// eslint-disable-next-line
const calculateServiceTotals = calculateSaleTotals;

const getDataWithTotal = (items) => {
  if (items.length === 0) return [];
  const typeTotal = items.reduce(
    (acc, item) => {
      acc.netValue += parseFloat(item.netValue || 0);
      acc.grossValue += parseFloat(item.grossValue || 0);
      acc.taxAmount += parseFloat(item.taxAmount || 0);
      if (item.itemType === "Rent") {
        acc.actualSecurityAmt += parseFloat(item.actualSecurityAmt || 0);
        acc.securityAmt += parseFloat(item.securityAmt || 0);
      }
      return acc;
    },
    {
      netValue: 0,
      grossValue: 0,
      taxAmount: 0,
      actualSecurityAmt: 0,
      securityAmt: 0,
    },
  );
  return [
    ...items,
    {
      isTotal: true,
      local_id: "total",
      netValue: typeTotal.netValue.toFixed(2),
      grossValue: typeTotal.grossValue.toFixed(2),
      taxAmount: typeTotal.taxAmount.toFixed(2),
      actualSecurityAmt: typeTotal.actualSecurityAmt.toFixed(2),
      securityAmt: typeTotal.securityAmt.toFixed(2),
    },
  ];
};

const getDataWithTotalNursing = (items) => {
  if (items.length === 0) return [];

  const typeTotal = items.reduce(
    (acc, item) => {
      acc.netValue += parseFloat(item.netValue || 0);
      acc.grossValue += parseFloat(item.grossValue || 0);
      acc.taxAmount += parseFloat(item.taxAmount || 0);
      return acc;
    },
    { netValue: 0, grossValue: 0, taxAmount: 0 },
  );

  return [
    ...items,
    {
      isTotal: true,
      local_id: "total",
      netValue: typeTotal.netValue.toFixed(2),
      grossValue: typeTotal.grossValue.toFixed(2),
      taxAmount: typeTotal.taxAmount.toFixed(2),
    },
  ];
};

// ── Outside component (no state deps) ─────────────────────────────
const getRentOnlyColumns = (
  privilegeSettings,
  handleChange,
  removeRow,
  isHospital = false,
) => {
  const canEditAmount = privilegeSettings.editAmount;
  // eslint-disable-next-line
  const canEditTax = privilegeSettings.editTax;

  return [
    {
      name: "Product Code",
      width: "180px",
      cell: (row) =>
        row.isTotal ? (
          <div className="fw-bold text-end w-100">Total</div>
        ) : (
          <InputField
            className="inputBox"
            name="materialCode"
            value={row.materialCode}
            disabled
          />
        ),
    },
    {
      name: "Product Details",
      width: "250px",
      cell: (row) =>
        row.isTotal ? null : (
          <TextAreaField
            className="inputBox"
            value={row.materialDescription}
            disabled
          />
        ),
    },
    {
      name: "Rent Slab",
      width: "120px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField className="inputBox" value={row.rentSlab} disabled />
        ),
    },
    {
      name: "From",
      width: "150px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="date"
            value={row.fromDate}
            disabled
          />
        ),
    },
    {
      name: "To",
      width: "150px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="date"
            value={row.toDate}
            disabled
          />
        ),
    },
    {
      name: "Days",
      width: "100px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            value={row.days}
            disabled
          />
        ),
    },
    {
      name: "Qty",
      width: "90px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            name="orderedQuantity"
            value={row.orderedQuantity}
            onChange={(e) => handleChange(row.local_id, e, "Rent")}
            disabled
          />
        ),
    },
    {
      name: "MRP",
      width: "120px",
      omit: true,
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            value={row.masterMRP || 0}
            disabled
          />
        ),
    },

    //  RENT-SPECIFIC LABEL
    {
      name: "Rate/Day (₹)",
      width: "130px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            name="sellingPrice"
            value={row.sellingPrice}
            onChange={(e) => handleChange(row.local_id, e, "Rent")}
            disabled={!canEditAmount}
          />
        ),
    },

    {
      name: "Net Value",
      width: "140px",
      cell: (row) =>
        row.isTotal ? (
          <div className="fw-bold">{row.netValue}</div>
        ) : (
          <InputField
            className="inputBox"
            type="number"
            value={row.netValue}
            disabled
          />
        ),
    },
    {
      name: "GST (%)",
      width: "100px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            name="taxPercentage"
            value={row.taxPercentage}
            onChange={(e) => handleChange(row.local_id, e, "Rent")}
            disabled={true}
          />
        ),
    },
    {
      name: "Tax Amount",
      width: "120px",
      cell: (row) =>
        row.isTotal ? (
          <div className="fw-bold">{row.taxAmount}</div>
        ) : (
          <InputField
            className="inputBox"
            type="number"
            value={row.taxAmount}
            disabled
          />
        ),
    },

    //  RENT: Total is editable
    {
      name: "Total (₹)",
      width: "130px",
      cell: (row) =>
        row.isTotal ? (
          <div className="fw-bold">{row.grossValue}</div>
        ) : (
          <InputField
            className="inputBox"
            type="number"
            name="grossValue"
            value={row.grossValue}
            onChange={(e) => handleChange(row.local_id, e, "Rent")}
            disabled={!canEditAmount}
          />
        ),
    },

    {
      name: "Actual Security",
      width: "110px",
      omit: isHospital,
      cell: (row) =>
        row.isTotal ? (
          <div className="fw-bold">{row.actualSecurityAmt}</div>
        ) : (
          <InputField
            className="inputBox"
            type="number"
            name="actualSecurityAmt"
            value={row.actualSecurityAmt}
            onChange={(e) => handleChange(row.local_id, e, "Rent")}
            // disabled={!canEditAmount}
            disabled={true}
          />
        ),
    },
    {
      name: "Security",
      width: "110px",
      omit: isHospital,
      cell: (row) =>
        row.isTotal ? (
          <div className="fw-bold">{row.securityAmt}</div>
        ) : (
          <InputField
            className="inputBox"
            type="number"
            name="securityAmt"
            value={row.securityAmt}
            onChange={(e) => handleChange(row.local_id, e, "Rent")}
            disabled={!canEditAmount}
          />
        ),
    },
    {
      name: "Action",
      width: "80px",
      cell: (row) =>
        row.isTotal ? null : (
          <button
            className="btn btn-danger btn-sm mt-1"
            onClick={() => removeRow(row.local_id, "Rent")}
          >
            <i className="bi bi-trash3"></i>
          </button>
        ),
    },
  ];
};

const getSaleOnlyColumns = (
  privilegeSettings,
  handleChange,
  removeRow,
  type = "Sale",
) => {
  const canEditAmount = privilegeSettings.editAmount;
  const canEditTax = privilegeSettings.editTax;

  return [
    {
      name: "Product Code",
      width: "180px",
      cell: (row) =>
        row.isTotal ? (
          <div className="fw-bold text-end w-100">Total</div>
        ) : (
          <InputField
            className="inputBox"
            name="materialCode"
            value={row.materialCode}
            disabled
          />
        ),
    },
    {
      name: "Product Details",
      width: "250px",
      cell: (row) =>
        row.isTotal ? null : (
          <TextAreaField
            className="inputBox"
            value={row.materialDescription}
            disabled
          />
        ),
    },
    {
      name: "Qty",
      width: "100px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            value={row.orderedQuantity}
            disabled
          />
        ),
    },
    {
      name: "MRP (₹)",
      width: "120px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            value={row.masterMRP || 0}
            disabled
          />
        ),
    },
    {
      name: "Master Price (₹)",
      width: "130px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            value={row.masterSellingPrice || 0}
            disabled
            title="Reference only"
          />
        ),
    },

    //  SALE-SPECIFIC: Unit Price is always read-only, derived from Total/Qty
    {
      name: "Unit Price (₹)",
      width: "130px",
      omit: true,
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            value={
              row.orderedQuantity && parseFloat(row.orderedQuantity) > 0
                ? parseFloat(
                    (
                      parseFloat(row.grossValue || 0) /
                      parseFloat(row.orderedQuantity)
                    ).toFixed(2),
                  )
                : 0
            }
            disabled // Always read-only in Sale
            title="Auto-calculated: Total ÷ Qty"
          />
        ),
    },

    {
      name: "Net Value",
      width: "140px",
      cell: (row) =>
        row.isTotal ? (
          <div className="fw-bold">{row.netValue}</div>
        ) : (
          <InputField
            className="inputBox"
            type="number"
            name="netValue"
            value={row.netValue}
            onChange={(e) => handleChange(row.local_id, e, "Sale")}
            // disabled
            disabled={!canEditAmount}
          />
        ),
    },
    {
      name: "GST (%)",
      width: "100px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            name="taxPercentage"
            value={row.taxPercentage}
            onChange={(e) => handleChange(row.local_id, e, "Sale")}
            disabled={!canEditTax}
          />
        ),
    },
    {
      name: "Tax Amount",
      width: "120px",
      cell: (row) =>
        row.isTotal ? (
          <div className="fw-bold">{row.taxAmount}</div>
        ) : (
          <InputField
            className="inputBox"
            type="number"
            value={row.taxAmount}
            disabled
          />
        ),
    },

    //  SALE: Total is the primary edit field
    {
      name: "Total (₹)",
      width: "130px",
      cell: (row) =>
        row.isTotal ? (
          <div className="fw-bold">{row.grossValue}</div>
        ) : (
          <InputField
            className="inputBox"
            type="number"
            name="grossValue"
            value={row.grossValue}
            onChange={(e) => handleChange(row.local_id, e, "Sale")}
            disabled={!canEditAmount}
          />
        ),
    },

    {
      name: "Action",
      width: "80px",
      cell: (row) =>
        row.isTotal ? null : (
          <button
            className="btn btn-danger btn-sm mt-1"
            onClick={() => removeRow(row.local_id, "Sale")}
          >
            <i className="bi bi-trash3"></i>
          </button>
        ),
    },
  ];
};

// ── SERVICE COLUMNS (with barcode) ────────────────────────────────────────────
const getServiceOnlyColumns = (privilegeSettings, handleChange, removeRow) => {
  const canEditAmount = privilegeSettings.editAmount;
  const canEditTax = privilegeSettings.editTax;

  return [
    {
      name: "Product Code",
      width: "180px",
      cell: (row) =>
        row.isTotal ? (
          <div className="fw-bold text-end w-100">Total</div>
        ) : (
          <InputField
            className="inputBox"
            name="materialCode"
            value={row.materialCode}
            disabled
          />
        ),
    },
    {
      name: "Product Details",
      width: "250px",
      cell: (row) =>
        row.isTotal ? null : (
          <TextAreaField
            className="inputBox"
            value={row.materialDescription}
            disabled
          />
        ),
    },

    // ── BARCODE COLUMN (Service-specific) ────────────────────────────────
    {
      name: "Barcode",
      width: "180px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            name="barcode"
            value={row.barcode || ""}
            onChange={(e) => handleChange(row.local_id, e, "Service")}
            placeholder="Enter barcode"
          />
        ),
      omit: true,
    },
    {
      name: "Qty",
      width: "100px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            value={row.orderedQuantity}
            disabled
          />
        ),
    },
    {
      name: "MRP (₹)",
      width: "120px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            value={row.masterMRP || 0}
            disabled
          />
        ),
    },
    {
      name: "Master Price (₹)",
      width: "130px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            value={row.masterSellingPrice || 0}
            disabled
            title="Reference only"
          />
        ),
    },
    {
      name: "Unit Price (₹)",
      width: "130px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            value={
              row.orderedQuantity && parseFloat(row.orderedQuantity) > 0
                ? parseFloat(
                    (
                      parseFloat(row.grossValue || 0) /
                      parseFloat(row.orderedQuantity)
                    ).toFixed(2),
                  )
                : 0
            }
            disabled
            title="Auto-calculated: Total ÷ Qty"
          />
        ),
    },
    {
      name: "Net Value",
      width: "140px",
      cell: (row) =>
        row.isTotal ? (
          <div className="fw-bold">{row.netValue}</div>
        ) : (
          <InputField
            className="inputBox"
            type="number"
            name="netValue"
            value={row.netValue}
            onChange={(e) => handleChange(row.local_id, e, "Service")}
            disabled={!canEditAmount}
          />
        ),
    },
    {
      name: "GST (%)",
      width: "100px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            name="taxPercentage"
            value={row.taxPercentage}
            onChange={(e) => handleChange(row.local_id, e, "Service")}
            disabled={!canEditTax}
          />
        ),
    },
    {
      name: "Tax Amount",
      width: "120px",
      cell: (row) =>
        row.isTotal ? (
          <div className="fw-bold">{row.taxAmount}</div>
        ) : (
          <InputField
            className="inputBox"
            type="number"
            value={row.taxAmount}
            disabled
          />
        ),
    },
    {
      name: "Total (₹)",
      width: "130px",
      cell: (row) =>
        row.isTotal ? (
          <div className="fw-bold">{row.grossValue}</div>
        ) : (
          <InputField
            className="inputBox"
            type="number"
            name="grossValue"
            value={row.grossValue}
            onChange={(e) => handleChange(row.local_id, e, "Service")}
            disabled={!canEditAmount}
          />
        ),
    },
    {
      name: "Action",
      width: "80px",
      cell: (row) =>
        row.isTotal ? null : (
          <button
            className="btn btn-danger btn-sm mt-1"
            onClick={() => removeRow(row.local_id, "Service")}
          >
            <i className="bi bi-trash3"></i>
          </button>
        ),
    },
  ];
};

const getNursingOnlyColumns = (
  privilegeSettings,
  handleChange,
  removeRow,
  isHospital = false,
) => {
  const canEditAmount = privilegeSettings.editAmount;
  const canEditTax = privilegeSettings.editTax;

  return [
    {
      name: "Product Code",
      width: "180px",
      cell: (row) =>
        row.isTotal ? (
          <div className="fw-bold text-end w-100">Total</div>
        ) : (
          <InputField
            className="inputBox"
            name="materialCode"
            value={row.materialCode}
            disabled
          />
        ),
    },
    {
      name: "Product Details",
      width: "250px",
      cell: (row) =>
        row.isTotal ? null : (
          <TextAreaField
            className="inputBox"
            value={row.materialDescription}
            disabled
          />
        ),
    },
    {
      name: "Rent Slab",
      width: "120px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField className="inputBox" value={row.rentSlab} disabled />
        ),
    },
    {
      name: "From",
      width: "150px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="date"
            value={row.fromDate}
            disabled
          />
        ),
    },
    {
      name: "To",
      width: "150px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="date"
            value={row.toDate}
            disabled
          />
        ),
    },
    {
      name: "Days",
      width: "100px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            value={row.days}
            disabled
          />
        ),
    },
    {
      name: "Qty",
      width: "100px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            name="orderedQuantity"
            value={row.orderedQuantity}
            onChange={(e) => handleChange(row.local_id, e, "Nursing")}
            disabled
          />
        ),
    },
    {
      name: "MRP",
      width: "120px",
      omit: true,
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            value={row.masterMRP || 0}
            disabled
          />
        ),
    },
    {
      name: "Rate/Day (₹)",
      width: "130px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            name="sellingPrice"
            value={row.sellingPrice}
            onChange={(e) => handleChange(row.local_id, e, "Nursing")}
            disabled={!canEditAmount}
          />
        ),
    },
    {
      name: "Net Value",
      width: "140px",
      cell: (row) =>
        row.isTotal ? (
          <div className="fw-bold">{row.netValue}</div>
        ) : (
          <InputField
            className="inputBox"
            type="number"
            value={row.netValue}
            disabled
          />
        ),
    },
    {
      name: "GST (%)",
      width: "100px",
      cell: (row) =>
        row.isTotal ? null : (
          <InputField
            className="inputBox"
            type="number"
            name="taxPercentage"
            value={row.taxPercentage}
            onChange={(e) => handleChange(row.local_id, e, "Nursing")}
            disabled={!canEditTax}
          />
        ),
    },
    {
      name: "Tax Amount",
      width: "120px",
      cell: (row) =>
        row.isTotal ? (
          <div className="fw-bold">{row.taxAmount}</div>
        ) : (
          <InputField
            className="inputBox"
            type="number"
            value={row.taxAmount}
            disabled
          />
        ),
    },
    {
      name: "Total (₹)",
      width: "130px",
      cell: (row) =>
        row.isTotal ? (
          <div className="fw-bold">{row.grossValue}</div>
        ) : (
          <InputField
            className="inputBox"
            type="number"
            name="grossValue"
            value={row.grossValue}
            onChange={(e) => handleChange(row.local_id, e, "Nursing")}
            disabled={!canEditAmount}
          />
        ),
    },
    {
      name: "Action",
      width: "80px",
      cell: (row) =>
        row.isTotal ? null : (
          <button
            className="btn btn-danger btn-sm mt-1"
            onClick={() => removeRow(row.local_id, "Nursing")}
          >
            <i className="bi bi-trash3"></i>
          </button>
        ),
    },
  ];
};

const CreateERF = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // eslint-disable-next-line
  const userData = useSelector((state) => state?.user?.data) || {};
  const currentUserId = userData.userId || "M000";

  const [adminUser, setAdminUserRoleData] = useState();

  // =============================================================================
  // HELPER FUNCTIONS
  // =============================================================================

  const getErfTypeByName = (name) => {
    return ERF_TYPES.find((type) => type.name === name);
  };

  const REMINDER_DAYS_BEFORE_END = 3; // constant — change here affects everything

  const calculateReminderDate = (endDate, days) => {
    if (!endDate) return "";

    const daysCount = parseInt(days) || 0;
    const end = new Date(endDate);
    const reminderDate = new Date(end);

    if (daysCount <= REMINDER_DAYS_BEFORE_END) {
      // SHORT RENTAL: days ≤ 3
      // Reminder = toDate + 1 day (day after rental ends)
      // e.g. 1 day: ends 24-Feb → remind 25-Feb
      // e.g. 2 days: ends 25-Feb → remind 26-Feb
      // e.g. 3 days: ends 26-Feb → remind 27-Feb
      reminderDate.setDate(reminderDate.getDate() + 1);
    } else {
      // NORMAL RENTAL: days > 3
      // Reminder = toDate - 3 days (standard advance notice)
      // e.g. 10 days: ends 05-Mar → remind 02-Mar
      reminderDate.setDate(reminderDate.getDate() - REMINDER_DAYS_BEFORE_END);
    }

    return reminderDate.toISOString().split("T")[0];
  };

  const convertToNumbers = (obj) => {
    const converted = { ...obj };

    const numericFields = [
      "orderedQuantity",
      "sellingPrice",
      "rentPerDay",
      "rent7Days",
      "rent15Days",
      "rent30Days",
      "days",
      "netValue",
      "grossValue",
      "taxPercentage",
      "taxAmount",
      "actualSecurityAmt",
      "securityAmt",
      "masterMRP",
      "masterSellingPrice",
      "masterUnitPrice",
      "masterTaxPercent",
      "refundAmt",
      "refundDays",
    ];

    numericFields.forEach((field) => {
      if (converted[field] !== undefined && converted[field] !== "") {
        converted[field] = parseFloat(converted[field]) || 0;
      }
    });

    return converted;
  };

  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line
  const [disabled, setDisabled] = useState(false);
  const [isFormEnabled, setIsFormEnabled] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [getDropdownData, setDropdownData] = useState("");
  const [activeItemType, setActiveItemType] = useState("Rent");
  const [activeItemTypeCode, setActiveItemTypeCode] = useState("Rent");

  // Accordion Toggles
  const [isCardVisible, setCardVisible] = useState(true);
  const [isCardVisible2, setCardVisible2] = useState(true);
  const [isCardVisible3, setCardVisible3] = useState(true);
  const [visible, setVisible] = useState({
    PAY: true,
    SEC: true,
    SMOD: true,
  });

  // Modal State
  const [getShowFailureModal, setShowFailureModal] = useState(false);
  const [getErrorFields, setErrorFields] = useState([]);
  const [failureModalData] = useState({
    title: "Validation Error",
    ErrorMsg: "Please check the required fields.",
  });

  // Add these to your existing state declarations
  const [erfForType, setErfForType] = useState("Patient"); // "Patient" or "Hospital"
  const [selectedHospital, setSelectedHospital] = useState(null);
  const [erfHospitalList, setErfHospitalList] = useState([]);
  const [isErfLoadingHospitals, setErfIsLoadingHospitals] = useState(false);

  // Address copy checkbox
  const [isSameAddress, setIsSameAddress] = useState(false);

  // Price Violation & Approval
  const [priceViolations, setPriceViolations] = useState([]);
  // const [acknowledgedViolations, setAcknowledgedViolations] = useState(
  //   new Set(),
  // ); //  Track which items had violations acknowledged
  const [showApprovalRequired, setShowApprovalRequired] = useState(false);

  // Data State
  const [hospitalList, setHospitalList] = useState([]); // Dynamic Hospital List

  const [rentItems, setRentItems] = useState([]);
  const [saleItems, setSaleItems] = useState([]); // reused for Service Items

  const [nursingItems, setNursingItems] = useState([]);

  const [validation, setValidation] = useState({});

  const [doctorsList, setDoctorsList] = useState([]);
  const [isLoadingHospitals, setIsLoadingHospitals] = useState(false);
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(false);

  const [costCenterList, setCostCenterList] = useState([]);
  const [isLoadingCostCenter, setIsLoadingCostCenter] = useState(false);

  const [patientList, setPatientList] = useState([]); // Dynamic Patient List
  // eslint-disable-next-line
  const [isLoadingPatient, setIsLoadingPatient] = useState(false);

  const [selectedNurse, setSelectedNurse] = useState(null);
  const [erfNurseList, setErfNurseList] = useState([]); // Dynamic Nurse List
  const [isLoadingErfNurse, setIsLoadingErfNurse] = useState(false);
  // eslint-disable-next-line
  const [selectedERF, setSelectedERF] = useState(null);
  // eslint-disable-next-line
  const [isERFLoaded, setIsERFLoaded] = useState(false);

  const [companyList, setCompanyList] = useState([]);
  // eslint-disable-next-line
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const [isPCDUser, setIsPCDUser] = useState(false);

  const [isSalesUser, setIsSalesUser] = useState(false);

  // Main Form Data
  const [formInputs, setFormInputs] = useState({
    // Order Details
    erfType: "",
    erfTypeCode: "",
    erfTypeDescription: "",
    orderDate: getTodaysData(),
    salePoint: userData?.organisationName || "",
    costCenter: userData?.firstName || "",
    erfCostCenterSubGroup: "SALESTEAM", // "SALESTEAM" | "HOSPITAL"
    privilegeCase: "Select",
    billingName: "Patient",
    orderType: "Select",
    sourceOfCall: "Select",

    // ── Company Fields ──────────────────────────────
    companyName: "",
    companyOrganisation: "",
    companyNr: "",
    companyCode: "",

    // Doctor Details
    doctorName: "",
    drType: "Select",
    drListedType: "Select", //  "Doctor" | "Hospital" — only relevant when drType === "Listed"
    drCode: "",
    patientErfHospitalName: "",

    patientType: "Select",
    patient: "",

    // Patient Details
    patientName: "",
    mobileNr: "",
    age: "",
    weight: "",
    gender: "",
    gstNo: "",
    panNo: "",
    isDiabetic: "No",
    aadharNo: "",
    patientChallenges: "",
    AssociateNumber: "",
    email: "",
    address: "",
    address2: "",
    city: "",

    hospitalName: "",
    hospitalAssociateNumber: "",
    hospitalMobileNr: "",
    hospitalEmail: "",
    hospitalGstNo: "",
    hospitalAddress: "",
    hospitalAddress2: "",
    hospitalCity: "",
    hospitalState: "",
    hospitalPinCode: "",
    hospitalBillingAddress: "",

    // Caregiver Details
    caregiverName: "",
    caregiverMobileNr: "",
    caregiverRelation: "",
    idProof: "Select",
    idProofNumber: "",
    emailId: "",

    // Address
    billingAddress: "",
    billingState: "Select",
    billingPinCode: "",
    deliveryAddress: "",
    deliveryState: "Select",
    deliveryPinCode: "",

    // Payment Details (Left)
    cartage: "",
    installationCharges: "",
    receivedAmount: "",

    // Payment Breakup (Right)
    paymentMode: "Select",
    cashAmount: "",
    balanceAmtBreakup: 0,
    chequeNo: "NA",
    cardTransNo: "NA",
    bankName: "NA",
    receiptNo: "NA",

    // Security Details (Left)
    standardSecurityAmt: 0,
    receivedSecurityAmt: "",

    // Security Breakup (Right)
    securityPincode: "",
    securityAmtMode: "Select",
    securityCashAmt: "",
    balanceSecurityBreakup: 0,
    securityChequeNo: "NA",
    securityCardTransNo: "NA",
    securityBankName: "NA",

    // Security Mode
    securityMode: "Select",
    securityModeCash: "",
    securityModeBal: 0,
    remarks: "",

    // Approval process
    approvalLevel: "1", // Stores "1" or "2"
    approvers: [],
    version: "V0",

    expectedDeliveryDate: getTodaysData(),
    expectedDeliveryTime: "",

    // ── Nurse Details  ──
    nurseUserId: "", // actual userId from the nurse record
    nurseName: "", // display name
    nurseAssociateNumber: "",
    nurseMobileNr: "",
    nurseEmail: "",
    nurseAddress: "",
    nurseAddress2: "",
    nurseCity: "",
    nursePanNo: "", // was being stored in gstNo incorrectly
    nurseBillingAddress: "",
    nurseBillingState: "",
    nurseBillingPinCode: "",
    serviceProviderCode: "",
    serviceProviderName: "",
  });

  // console.log("FormInputs", formInputs);

  // console.log("Rent Items", rentItems);

  const [globalRentConfig, setGlobalRentConfig] = useState({
    rentSlab: "Per Day",
    rentSlabCode: "PER_DAY",
    rentSlabDescription: "PER_DAY",
    fromDate: getTodaysData(),
    toDate: getTodaysData(),
    days: 1,
  });
  // =============================================================================
  // DERIVED STATE & OPTIONS
  // =============================================================================

  const erfTypeOptions = useMemo(() => ERF_TYPES, []);

  const paymentModeDropdown = useMemo(
    () =>
      Object.values(PAYMENT_MODES).map((mode, i) => ({
        id: i + 1,
        name: mode,
      })),
    [],
  );

  const privilegeOptions = useMemo(() => {
    // Find the ERF object that matches the selected name
    const selectedType = ERF_TYPES.find(
      (type) => type.name === formInputs.erfType,
    );

    // Return its embedded privileges, or empty array if not found
    return selectedType?.privileges || [];
  }, [formInputs.erfType]);

  // Calculate Totals
  const allItems = useMemo(
    () => [...rentItems, ...saleItems, ...nursingItems],
    [rentItems, saleItems, nursingItems],
  );

  const totals = useMemo(() => {
    return allItems.reduce(
      (acc, item) => {
        acc.net += parseFloat(item.netValue || 0);
        acc.tax += parseFloat(item.taxAmount || 0);
        acc.gross += parseFloat(item.grossValue || 0); // ← NEW: track exact gross
        if (item.itemType === "Rent") {
          acc.security += parseFloat(item.securityAmt || 0);
          acc.standardSecurityAmt += parseFloat(item.actualSecurityAmt || 0);
        }
        return acc;
      },
      { net: 0, tax: 0, gross: 0, security: 0, standardSecurityAmt: 0 },
    );
  }, [allItems]);

  const itemTypeTotals = useMemo(() => {
    const sumItems = (items) =>
      items.reduce(
        (acc, item) => {
          acc.netTotal += parseFloat(item.netValue || 0);
          acc.taxTotal += parseFloat(item.taxAmount || 0);
          acc.grossTotal += parseFloat(item.grossValue || 0);
          acc.securityTotal += parseFloat(item.securityAmt || 0);
          return acc;
        },
        { netTotal: 0, taxTotal: 0, grossTotal: 0, securityTotal: 0 },
      );

    // saleItems holds both Sale and Service — split them
    const saleOnly = saleItems.filter((i) => i.itemType === "Sale");
    const serviceOnly = saleItems.filter((i) => i.itemType === "Service");

    const rent = sumItems(rentItems);
    const sale = sumItems(saleOnly);
    const service = sumItems(serviceOnly);
    const nursing = sumItems(nursingItems);

    const fmt = (n) => parseFloat(n.toFixed(2));

    return {
      // ── Rent ──────────────────────────────────────────────────────────
      rentNetTotal: fmt(rent.netTotal),
      rentTaxTotal: fmt(rent.taxTotal),
      rentGrossTotal: fmt(rent.grossTotal),
      rentSecurityTotal: fmt(rent.securityTotal),

      // ── Sale ──────────────────────────────────────────────────────────
      saleNetTotal: fmt(sale.netTotal),
      saleTaxTotal: fmt(sale.taxTotal),
      saleGrossTotal: fmt(sale.grossTotal),

      // ── Service ───────────────────────────────────────────────────────
      serviceNetTotal: fmt(service.netTotal),
      serviceTaxTotal: fmt(service.taxTotal),
      serviceGrossTotal: fmt(service.grossTotal),

      // ── Nursing ───────────────────────────────────────────────────────
      nursingNetTotal: fmt(nursing.netTotal),
      nursingTaxTotal: fmt(nursing.taxTotal),
      nursingGrossTotal: fmt(nursing.grossTotal),

      // ── Overall Line Item Totals ───────────────────────────────────────
      lineItemNetTotal: fmt(
        rent.netTotal + sale.netTotal + service.netTotal + nursing.netTotal,
      ),
      lineItemTaxTotal: fmt(
        rent.taxTotal + sale.taxTotal + service.taxTotal + nursing.taxTotal,
      ),
      lineItemGrossTotal: fmt(
        rent.grossTotal +
          sale.grossTotal +
          service.grossTotal +
          nursing.grossTotal,
      ),
      lineItemGrossRounded: Math.round(
        rent.grossTotal +
          sale.grossTotal +
          service.grossTotal +
          nursing.grossTotal,
      ),
      roundOffAmount: fmt(
        Math.round(
          rent.grossTotal +
            sale.grossTotal +
            service.grossTotal +
            nursing.grossTotal,
        ) -
          (rent.grossTotal +
            sale.grossTotal +
            service.grossTotal +
            nursing.grossTotal),
      ),
    };
  }, [rentItems, saleItems, nursingItems]);

  const totalGrossAmount = totals.gross;

  // "Total Amount" field = line items rounded to nearest rupee
  const totalPayable = Math.round(totalGrossAmount); // round -off

  // "Net Amount" field = rounded total + cartage + installation
  const netAmount = (
    totalPayable +
    (parseFloat(formInputs.cartage) || 0) +
    (parseFloat(formInputs.installationCharges) || 0)
  ).toFixed(2);

  const balanceAmount = (
    parseFloat(netAmount) - (parseFloat(formInputs.receivedAmount) || 0)
  ).toFixed(2);

  const balanceSecurityAmt = (
    totals.security - (parseFloat(formInputs.receivedSecurityAmt) || 0)
  ).toFixed(2);

  // =============================================================================
  // EFFECTS
  // =============================================================================

  useEffect(() => {
    if (!userData) return;

    const allowedRoles = ["SALES-ADMIN"];
    const isAllowed = userData?.roles?.some((r) =>
      allowedRoles.includes(r.roleId),
    );
    const isPCD = userData?.roles?.some((r) => r.roleId === "SALES-PCD");
    const isSales = !isAllowed && !isPCD;

    setAdminUserRoleData(isAllowed);
    setIsPCDUser(isPCD);
    setIsSalesUser(isSales);

    setFormInputs((prev) => ({
      ...prev,
      salePoint: userData.firstName || "",
      // Non-PCD users are locked to their own name; PCD can select from list
      costCenter: isPCD ? prev.costCenter : userData.firstName || "",
    }));

    // Fetch the API-driven list (needed for PCD's dropdown)
    fetchCostCenterList();
    // eslint-disable-next-line
  }, [userData]);

  // Enable/Disable Form
  useEffect(() => {
    const isEnabled =
      formInputs.erfType !== "" &&
      formInputs.erfType !== "Select" &&
      formInputs.privilegeCase !== "Select";
    setIsFormEnabled(isEnabled);
  }, [formInputs.erfType, formInputs.privilegeCase]);

  // Handle Dropdown Selections
  useEffect(() => {
    if (getDropdownData) {
      const { textField, name } = getDropdownData;

      if (!name || name === "" || name === "Select") return;

      const currentErfHospitalList = erfHospitalList;
      const currentErfNurseList = erfNurseList;
      const currentCompanyList = companyList;
      const currentDoctorsList = doctorsList;
      const currentPatientList = patientList;

      setFormInputs((prev) => {
        const newData = { ...prev, [textField]: name };

        if (textField === "erfType") {
          const selectedErfType = getErfTypeByName(name);
          if (selectedErfType) {
            newData.erfTypeCode = selectedErfType.code;
            newData.erfTypeDescription = selectedErfType.description;
          }
          newData.privilegeCase = "Select";
        }

        if (textField === "selectedCompanyId") {
          const company = currentCompanyList.find((c) => c.name === name);
          if (company) {
            setSelectedCompany(company);
            newData.companyName = company.companyName;
            newData.companyOrganisation = company.organisationName;
            newData.companyNr = company.companyNr;
            newData.companyCode = company.companyCode;
          }
        }

        if (textField === "costCenter") {
          newData.costCenterCode = getDropdownData?.code || "";
        }

        if (textField === "drType") {
          // Reset all doctor-related fields on type change
          newData.drListedType = "Select";
          newData.patientErfHospitalName = "";
          newData.doctorName = "";
          newData.drCode = "";
          setHospitalList([]);
          setDoctorsList([]);

          // If Not Given → clear and don't allow editing
          if (name === "Not Given") {
            newData.doctorName = "";
            newData.drCode = "";
          }
        }

        if (textField === "drListedType") {
          // Reset downstream fields when sub-type changes
          newData.patientErfHospitalName = "";
          newData.doctorName = "";
          newData.drCode = "";
          setHospitalList([]);
          setDoctorsList([]);

          if (name === "Doctor") {
            fetchAllDoctors(); // load all doctors into doctorsList
          } else if (name === "Hospital") {
            fetchHospitalList(); // load hospitals into hospitalList
          }
        }

        if (textField === "patientType") {
          newData.patient = "";
          setPatientList([]);
          if (name === "Listed") {
            fetchPatientList();
          }
        }

        // When hospital is selected (under Listed --> Hospital flow)
        if (textField === "patientErfHospitalName") {
          newData.doctorName = "";
          newData.drCode = "";
          if (name) {
            fetchDoctorsByHospital(name); // load doctors for this hospital
          }
        }

        // When doctor is selected from dropdown
        if (textField === "doctorName") {
          const selectedDoc = currentDoctorsList.find(
            (doc) => doc.name === name,
          );
          if (selectedDoc) {
            newData.drCode = selectedDoc.userId || selectedDoc.code || "";
            if (!newData.patientErfHospitalName && selectedDoc.hospitalName) {
              newData.patientErfHospitalName = selectedDoc.hospitalName;
            }
          }
        }

        // Reset ERF Type / Privilege
        if (textField === "erfType" || textField === "privilegeCase") {
          setRentItems([]);
          setSaleItems([]);
          setNursingItems([]);
          setPriceViolations([]); // clear stale violations
          setShowApprovalRequired(false); // hide banner
          newData.approvers = [];

          newData.cartage = 0;
          newData.installationCharges = 0;
          newData.receivedAmount = 0;
          newData.paymentMode = "Select";
          newData.cashAmount = 0;
          newData.balanceAmtBreakup = 0;
          newData.chequeNo = "NA";
          newData.cardTransNo = "NA";
          newData.bankName = "NA";
          newData.receiptNo = "NA";
          newData.receivedSecurityAmt = "";
          newData.securityPincode = "";
          newData.securityAmtMode = "Select";
          newData.securityCashAmt = "";
          newData.balanceSecurityBreakup = 0;
          newData.securityChequeNo = "NA";
          newData.securityCardTransNo = "NA";
          newData.securityBankName = "NA";
          newData.securityMode = "Select";
          newData.securityModeCash = "";
          newData.securityModeBal = 0;

          if (textField === "erfType" && prev.erfType !== name) {
            newData.privilegeCase = "Select";
          }
        }

        // Reset Payment Mode Logic
        if (textField === "paymentMode") {
          newData.cashAmount = "";
          newData.balanceAmtBreakup = 0;
          newData.chequeNo = "NA";
          newData.cardTransNo = "NA";
          newData.bankName = "NA";
          newData.receiptNo = "NA";

          if (name === "NR") {
            newData.receivedAmount = 0;
          }
        }

        // Reset Security Amt Mode Logic
        if (textField === "securityAmtMode") {
          newData.securityCashAmt = "";
          newData.balanceSecurityBreakup = 0;
          newData.securityChequeNo = "NA";
          newData.securityCardTransNo = "NA";
          newData.securityBankName = "NA";

          if (name === "NR") {
            newData.receivedSecurityAmt = 0;
          }
        }

        // Reset Security Mode Logic
        if (textField === "securityMode") {
          newData.securityModeCash = "";
          newData.securityModeBal = 0;
        }

        // Reset ID Proof
        if (textField === "idProof") {
          newData.idProofNumber = "";
        }

        if (textField === "patient") {
          const selectedPatient = currentPatientList.find(
            (p) => p.name === name,
          );

          if (selectedPatient) {
            //  Use employeeName as patientName (since API doesn't return patientName)
            newData.patientName =
              selectedPatient.employeeName ||
              selectedPatient.organisationName ||
              "";
            newData.mobileNr = selectedPatient.mobileNr || "";
            newData.email = selectedPatient.email || "";
            newData.aadharNo = selectedPatient.aadharNo || "";
            newData.panNo = selectedPatient.panNo || "";
            newData.AssociateNumber = selectedPatient.AssociateNumber || "";
            newData.gender = selectedPatient.patientGender || "";

            // Address fields
            newData.address = selectedPatient.address || "";
            newData.address2 = selectedPatient.address2 || "";
            newData.city = selectedPatient.city || "";

            // ---
            newData.age = selectedPatient.age || "";
            newData.weight = selectedPatient.weight || "";
            newData.patientChallenges = selectedPatient.patientChallenges || "";

            newData.caregiverName = selectedPatient.caregiverName || "";
            newData.caregiverMobileNr = selectedPatient.caregiverMobileNr || "";
            newData.emailId = selectedPatient.emailId || ""; // emailId key used for caregiver email
            newData.caregiverRelation = selectedPatient.caregiverRelation || "";
            newData.idProof = selectedPatient.idProof || "Select";
            newData.idProofNumber = selectedPatient.idProofNumber || "";

            const fullAddress = [
              selectedPatient.address,
              selectedPatient.address2,
              selectedPatient.city,
            ]
              .filter(Boolean)
              .join(", ");

            newData.billingAddress = fullAddress;
            newData.billingState = selectedPatient.state || "Select";
            newData.billingPinCode = selectedPatient.cityPostalCode || "";

            newData.deliveryAddress = fullAddress;
            newData.deliveryState = selectedPatient.state || "Select";
            newData.deliveryPinCode = selectedPatient.cityPostalCode || "";
          }
        }

        //  Handle Hospital Selection
        if (textField === "selectedHospitalId") {
          if (!name || name === "" || name === "Select") return newData;

          const erfHospital = currentErfHospitalList.find(
            (h) => h.name === name,
          );
          if (erfHospital) {
            setSelectedHospital(erfHospital);

            newData.hospitalName =
              erfHospital.hospitalName || erfHospital.organisationName || "";
            newData.hospitalAssociateNumber = erfHospital.AssociateNumber || "";
            newData.hospitalMobileNr = erfHospital.mobileNr || "";
            newData.hospitalEmail = erfHospital.email || "";
            newData.hospitalAddress = erfHospital.address || "";
            newData.hospitalAddress2 = erfHospital.address2 || "";
            newData.hospitalCity = erfHospital.city || "";
            newData.hospitalState = erfHospital.state || "";
            newData.hospitalPinCode = erfHospital.cityPostalCode || "";

            const gstCert = erfHospital.certifications?.find(
              (cert) => cert.documentTypeCode === "101",
            );
            newData.hospitalGstNo = gstCert ? gstCert.documentNumber : "";

            newData.hospitalBillingAddress = [
              erfHospital.address,
              erfHospital.address2,
              erfHospital.city,
            ]
              .filter(Boolean)
              .join(", ");

            // ── Cost Center = Hospital Name when ERF is through hospital ────────
            newData.costCenter =
              erfHospital.hospitalName || erfHospital.organisationName || "";
            newData.erfCostCenterSubGroup = "HOSPITAL";

            // ── Patient fields are NOT touched ──────────────────────────────────
          } else {
            console.error("Hospital not found:", name);
          }
        }

        if (textField === "selectedNurseId") {
          const erfNurse = currentErfNurseList.find(
            (nurse) => nurse.name === name,
          );

          if (erfNurse) {
            setSelectedNurse(erfNurse);

            // ── nurse keys ──────────────
            newData.nurseUserId = erfNurse.userId || erfNurse.id || "";
            newData.nurseName =
              erfNurse.employeeName || erfNurse.organisationName || "";
            newData.nurseAssociateNumber = erfNurse.AssociateNumber || "";
            newData.nurseMobileNr = erfNurse.mobileNr || "";
            newData.nurseEmail = erfNurse.email || "";
            newData.nurseAddress = erfNurse.address || "";
            newData.nurseAddress2 = erfNurse.address2 || "";
            newData.nurseCity = erfNurse.city || "";
            newData.serviceProviderName = erfNurse.serviceProviderName || "";
            newData.serviceProviderCode = erfNurse.serviceProviderCode || "";

            // Pan/GST — look for documentTypeCode "100"
            const panCert = erfNurse.certifications?.find(
              (cert) => cert.documentTypeCode === "100",
            );
            newData.nursePanNo = panCert ? panCert.documentNumber : "";

            newData.nurseBillingAddress = [
              erfNurse.address,
              erfNurse.address2,
              erfNurse.city,
            ]
              .filter(Boolean)
              .join(", ");

            newData.nurseBillingState = erfNurse.state || "";
            newData.nurseBillingPinCode = erfNurse.cityPostalCode || "";
          } else {
            console.error("Nurse not found:", name);
          }
        }

        return newData;
      });
    }
    // eslint-disable-next-line
  }, [getDropdownData]);

  useEffect(() => {
    if (rentItems.length === 0 && nursingItems.length === 0) {
      return;
    }

    const syncItems = (prevItems, itemType) =>
      prevItems.map((item) => {
        const updatedItem = {
          ...item,
          fromDate: globalRentConfig.fromDate,
          toDate: globalRentConfig.toDate,
          days: globalRentConfig.days,
          // Both Rent AND Nursing sync slab
          rentSlab: globalRentConfig.rentSlab,
          rentSlabCode: globalRentConfig.rentSlabCode,
          rentSlabDescription: globalRentConfig.rentSlabDescription,
        };
        const calculated = calculateRentTotals(updatedItem);
        // return { ...updatedItem, ...calculated };
        const finalItem = { ...updatedItem, ...calculated };

        // 3. Re-run violation check so priceViolations state stays accurate
        // Use setTimeout(0) to let setRentItems commit first, then check
        runViolationCheck(finalItem, itemType, finalItem.local_id);

        return finalItem;
      });

    if (rentItems.length > 0) {
      setRentItems(syncItems);
    }
    if (nursingItems.length > 0) {
      setNursingItems(syncItems);
    }
    // eslint-disable-next-line
  }, [globalRentConfig]);

  // Update Dynamic Balances
  useEffect(() => {
    const receivedAmount = parseFloat(formInputs.receivedAmount) || 0;
    const cashAmount = parseFloat(formInputs.cashAmount) || 0;
    const balanceAmtBreakup = parseFloat(
      (receivedAmount - cashAmount).toFixed(2),
    );

    const receivedSecurityAmt = parseFloat(formInputs.receivedSecurityAmt) || 0;
    const securityCashAmt = parseFloat(formInputs.securityCashAmt) || 0;
    const balanceSecurityBreakup = parseFloat(
      (receivedSecurityAmt - securityCashAmt).toFixed(2),
    );

    const securityModeCash = parseFloat(formInputs.securityModeCash) || 0;
    const securityModeBal = parseFloat(
      (parseFloat(balanceSecurityAmt) - securityModeCash).toFixed(2),
    );

    setFormInputs((prev) => ({
      ...prev,
      balanceAmtBreakup,
      balanceSecurityBreakup,
      securityModeBal,
    }));
  }, [
    formInputs.receivedAmount,
    formInputs.cashAmount,
    formInputs.receivedSecurityAmt,
    formInputs.securityCashAmt,
    balanceSecurityAmt,
    formInputs.securityModeCash,
  ]);

  // Auto-fill Cash Amt for Security Mode
  useEffect(() => {
    if (!formInputs.securityMode || formInputs.securityMode === "Select")
      return;

    const balSec = parseFloat(balanceSecurityAmt) || 0;

    if (["Cash", "Euro", "Dollar"].includes(formInputs.securityMode)) {
      setFormInputs((prev) => ({
        ...prev,
        securityModeCash: balSec,
        securityModeBal: 0,
      }));
    } else if (
      ["Card", "Cheque", "Online", "Paytm", "UPI", "Card&Cheque"].includes(
        formInputs.securityMode,
      )
    ) {
      setFormInputs((prev) => ({
        ...prev,
        securityModeCash: 0,
        securityModeBal: balSec.toString(),
      }));
    } else if (["Card&Cash", "Cheque&Cash"].includes(formInputs.securityMode)) {
      setFormInputs((prev) => ({
        ...prev,
        securityModeCash: "",
        securityModeBal: balSec.toString(),
      }));
    }
  }, [formInputs.securityMode, balanceSecurityAmt]);

  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================

  const toggleCardVisibility = () => setCardVisible(!isCardVisible);
  const toggleCardVisibility2 = () => setCardVisible2(!isCardVisible2);
  const toggleCardVisibility3 = () => setCardVisible3(!isCardVisible3);
  const toggleVisibility = (key) =>
    setVisible((prev) => ({ ...prev, [key]: !prev[key] }));

  //  INSIDE component — deps array is now clean
  const runViolationCheck = useCallback((updatedItem, itemType, local_id) => {
    const violations = detectPriceViolation(updatedItem, itemType); // pure fn, stable reference

    setPriceViolations((prevViolations) => {
      const filtered = prevViolations.filter((v) => v.itemId !== local_id);

      if (violations.length > 0) {
        setShowApprovalRequired(true);
        return [...filtered, ...violations];
      }

      // Price corrected for this item
      // setAcknowledgedViolations((prev) => {
      //   const newSet = new Set(prev);
      //   newSet.delete(local_id);
      //   return newSet;
      // });

      if (filtered.length === 0) {
        setShowApprovalRequired(false);
        setFormInputs((prev) => ({ ...prev, approvers: [] }));
      }
      return filtered;
    });
  }, []); //  Empty deps — only uses stable setState setters + pure fn outside component

  const removeItemRow = useCallback((local_id, type) => {
    if (type === "Rent") {
      setRentItems((prev) => prev.filter((item) => item.local_id !== local_id));
    } else if (type === "Nursing") {
      setNursingItems((prev) =>
        prev.filter((item) => item.local_id !== local_id),
      );
    } else {
      setSaleItems((prev) => prev.filter((item) => item.local_id !== local_id));
    }
    setPriceViolations((prev) => {
      const filtered = prev.filter((v) => v.itemId !== local_id);
      if (filtered.length === 0) {
        setShowApprovalRequired(false);
        setFormInputs((fi) => ({ ...fi, approvers: [] }));
      }
      return filtered;
    });
  }, []);

  const handleLineItemChange = useCallback(
    (local_id, e, type) => {
      const { name, value } = e.target;

      const updateLogic = (prev) =>
        prev.map((item) => {
          if (item.local_id !== local_id) return item;

          let updatedItem = { ...item, [name]: value };

          // ── BARCODE (Service only) — no recalc needed ──────────────────────
          if (name === "barcode") return updatedItem;

          // ════════════════════════════════════════════════════════════════════
          //  RENT
          // ════════════════════════════════════════════════════════════════════
          if (item.itemType === "Rent") {
            if (name === "grossValue") {
              // ── REVERSE CALC: User typed new Total ─────────────────────────
              const grossVal = parseFloat(value) || 0;
              const qty = parseFloat(updatedItem.orderedQuantity) || 1;
              const days = parseFloat(updatedItem.days) || 1;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;

              // Net = Gross / (1 + tax%)
              const netVal = parseFloat(
                (grossVal / (1 + tax / 100)).toFixed(2),
              );
              const taxAmt = parseFloat((grossVal - netVal).toFixed(2));
              // sellingPrice = net Rate/Day (tax-exclusive)
              const ratePerDay =
                days * qty > 0
                  ? parseFloat((netVal / (days * qty)).toFixed(4))
                  : 0;

              updatedItem.netValue = netVal;
              updatedItem.taxAmount = taxAmt;
              updatedItem.sellingPrice = ratePerDay;
              runViolationCheck(updatedItem, "Rent", local_id);
            } else if (name === "taxPercentage") {
              // Re-split existing net using new tax rate
              const netVal = parseFloat(updatedItem.netValue) || 0;
              const tax = parseFloat(value) || 0;
              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));
              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;
            } else if (name === "sellingPrice" || name === "orderedQuantity") {
              // ── FORWARD CALC: sellingPrice is net Rate/Day ─────────────────
              const qty = parseFloat(updatedItem.orderedQuantity) || 0;
              const days = parseFloat(updatedItem.days) || 0;
              const price = parseFloat(updatedItem.sellingPrice) || 0; // net rate/day
              const tax = parseFloat(updatedItem.taxPercentage) || 0;

              const netVal = parseFloat((price * days * qty).toFixed(2));
              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));

              updatedItem.netValue = parseFloat(netVal.toFixed(2));
              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;

              if (name === "sellingPrice") {
                runViolationCheck(updatedItem, "Rent", local_id);
              }
            }
          }

          // ════════════════════════════════════════════════════════════════════
          //  NURSING
          // ════════════════════════════════════════════════════════════════════
          else if (item.itemType === "Nursing") {
            if (name === "rentSlab") {
              // Slab changed — recalculate from scratch using master prices
              const calculated = calculateRentTotals(updatedItem);
              updatedItem = { ...updatedItem, ...calculated };
              runViolationCheck(updatedItem, "Nursing", local_id);
            } else if (name === "grossValue") {
              // REVERSE CALC
              const grossVal = parseFloat(value) || 0;
              const qty = parseFloat(updatedItem.orderedQuantity) || 1;
              const days = parseFloat(updatedItem.days) || 1;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;

              const netVal = parseFloat(
                (grossVal / (1 + tax / 100)).toFixed(2),
              );
              const taxAmt = parseFloat((grossVal - netVal).toFixed(2));
              const ratePerDay =
                days * qty > 0
                  ? parseFloat((netVal / (days * qty)).toFixed(4))
                  : 0;

              updatedItem.netValue = netVal;
              updatedItem.taxAmount = taxAmt;
              updatedItem.sellingPrice = ratePerDay;
              runViolationCheck(updatedItem, "Nursing", local_id);
            } else if (name === "taxPercentage") {
              const netVal = parseFloat(updatedItem.netValue) || 0;
              const tax = parseFloat(value) || 0;
              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));
              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;
            } else if (name === "sellingPrice" || name === "orderedQuantity") {
              // FORWARD CALC
              const qty = parseFloat(updatedItem.orderedQuantity) || 0;
              const days = parseFloat(updatedItem.days) || 0;
              const price = parseFloat(updatedItem.sellingPrice) || 0;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;

              let netVal = 0;
              switch (updatedItem.rentSlab) {
                case "Per Day":
                  netVal = price * days * qty;
                  break;
                case "7 Days":
                  netVal = (parseFloat(updatedItem.rent7Days) || 0) * qty;
                  break;
                case "15 Days":
                  netVal = (parseFloat(updatedItem.rent15Days) || 0) * qty;
                  break;
                case "30 Days":
                  netVal = (parseFloat(updatedItem.rent30Days) || 0) * qty;
                  break;
                default:
                  netVal = price * days * qty;
              }

              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));

              updatedItem.netValue = parseFloat(netVal.toFixed(2));
              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;

              if (name === "sellingPrice") {
                runViolationCheck(updatedItem, "Nursing", local_id);
              }
            }
          }

          // ════════════════════════════════════════════════════════════════════
          //  SALE
          // ════════════════════════════════════════════════════════════════════
          else if (item.itemType === "Sale") {
            if (name === "netValue") {
              // FORWARD MATH: user typed a new Net Value
              const netVal = parseFloat(value) || 0;
              const qty = parseFloat(updatedItem.orderedQuantity) || 1;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;
              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));
              const netUnitPrice =
                qty > 0 ? parseFloat((netVal / qty).toFixed(2)) : 0;

              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;
              updatedItem.sellingPrice = netUnitPrice;
              runViolationCheck(updatedItem, "Sale", local_id);
            } else if (name === "grossValue") {
              // REVERSE MATH: user typed new Total
              const grossVal = parseFloat(value) || 0;
              const qty = parseFloat(updatedItem.orderedQuantity) || 1;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;
              const netVal = parseFloat(
                (grossVal / (1 + tax / 100)).toFixed(2),
              );
              const taxAmt = parseFloat((grossVal - netVal).toFixed(2));
              const netUnitPrice =
                qty > 0 ? parseFloat((netVal / qty).toFixed(2)) : 0;

              updatedItem.netValue = netVal;
              updatedItem.taxAmount = taxAmt;
              updatedItem.sellingPrice = netUnitPrice;
              runViolationCheck(updatedItem, "Sale", local_id);
            } else if (name === "taxPercentage") {
              // Net stays fixed, recalculate gross
              const netVal = parseFloat(updatedItem.netValue) || 0;
              const tax = parseFloat(value) || 0;
              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));
              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;
            } else if (name === "sellingPrice" || name === "orderedQuantity") {
              // FORWARD CALC: sellingPrice is the net unit price
              const qty = parseFloat(updatedItem.orderedQuantity) || 0;
              const price = parseFloat(updatedItem.sellingPrice) || 0;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;
              const netVal = parseFloat((qty * price).toFixed(2));
              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));

              updatedItem.netValue = netVal;
              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;

              if (name === "sellingPrice") {
                runViolationCheck(updatedItem, "Sale", local_id);
              }
            }
          }

          // ════════════════════════════════════════════════════════════════════
          //  SERVICE  (identical math to Sale)
          // ════════════════════════════════════════════════════════════════════
          else if (item.itemType === "Service") {
            if (name === "netValue") {
              const netVal = parseFloat(value) || 0;
              const qty = parseFloat(updatedItem.orderedQuantity) || 1;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;
              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));
              const netUnitPrice =
                qty > 0 ? parseFloat((netVal / qty).toFixed(2)) : 0;

              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;
              updatedItem.sellingPrice = netUnitPrice;
              runViolationCheck(updatedItem, "Service", local_id);
            } else if (name === "grossValue") {
              const grossVal = parseFloat(value) || 0;
              const qty = parseFloat(updatedItem.orderedQuantity) || 1;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;
              const netVal = parseFloat(
                (grossVal / (1 + tax / 100)).toFixed(2),
              );
              const taxAmt = parseFloat((grossVal - netVal).toFixed(2));
              const netUnitPrice =
                qty > 0 ? parseFloat((netVal / qty).toFixed(2)) : 0;

              updatedItem.netValue = netVal;
              updatedItem.taxAmount = taxAmt;
              updatedItem.sellingPrice = netUnitPrice;
              runViolationCheck(updatedItem, "Service", local_id);
            } else if (name === "taxPercentage") {
              const netVal = parseFloat(updatedItem.netValue) || 0;
              const tax = parseFloat(value) || 0;
              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));
              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;
            } else if (name === "sellingPrice" || name === "orderedQuantity") {
              const qty = parseFloat(updatedItem.orderedQuantity) || 0;
              const price = parseFloat(updatedItem.sellingPrice) || 0;
              const tax = parseFloat(updatedItem.taxPercentage) || 0;
              const netVal = parseFloat((qty * price).toFixed(2));
              const taxAmt = parseFloat((netVal * (tax / 100)).toFixed(2));
              const grossVal = parseFloat((netVal + taxAmt).toFixed(2));

              updatedItem.netValue = netVal;
              updatedItem.taxAmount = taxAmt;
              updatedItem.grossValue = grossVal;

              if (name === "sellingPrice") {
                runViolationCheck(updatedItem, "Service", local_id);
              }
            }
          }

          return updatedItem;
        });

      // Route update to the correct state array
      if (type === "Rent") {
        setRentItems(updateLogic);
      } else if (type === "Nursing") {
        setNursingItems(updateLogic);
      } else {
        setSaleItems(updateLogic);
      }
    },
    [runViolationCheck], // runViolationCheck is stable (empty deps useCallback)
  );

  const handleInputChange = ({ target: { name, value } }) => {
    const errors = {};

    const dateFields = ["expectedDeliveryDate", "orderDate"];
    if (dateFields.includes(name) && value) {
      const today = new Date().toISOString().split("T")[0];
      if (value < today) {
        toast.error("Cannot select a past date");
        setValidation((prev) => ({
          ...prev,
          [name]: "Date cannot be in the past",
        }));
        return; // Block update
      } else {
        setValidation((prev) => ({ ...prev, [name]: "" }));
      }
    }

    const numericFields = [
      "cartage",
      "installationCharges",
      "receivedAmount",
      "cashAmount",
      "receivedSecurityAmt",
      "securityCashAmt",
      "securityModeCash",
      "age",
      "weight",
    ];

    if (numericFields.includes(name) && parseFloat(value) < 0) {
      toast.error(`${name} cannot be negative`);
      return;
    }

    setFormInputs((prev) => ({ ...prev, [name]: value }));

    if (name === "mobileNr") {
      !validateContactNumber(value)
        ? (errors.mobileNr = message.VALID_CONTACT_NUMBER)
        : (errors.mobileNr = "");
    }
    setValidation((prev) => ({ ...prev, [name]: errors[name] || "" }));
  };

  const handleApproverSelection = useCallback(
    (index, selectedData) => {
      //  STEP 1: Validate FIRST (before any state updates)

      // Check if trying to deselect/clear approver
      if (
        !selectedData ||
        !selectedData.name ||
        selectedData.name === "Select"
      ) {
        // Prevent deselecting if violations or approval required
        if (
          priceViolations.length > 0 ||
          APPROVAL_REQUIRED_CASES.includes(formInputs.privilegeCase)
        ) {
          // toast.error(
          //   "Cannot remove approver while violations/approvals are required",
          // );
          return; //  Exit early - don't update state
        }
        // If no violations, allow clearing
        return;
      }

      //  STEP 3: Update state (only if validations passed)
      setFormInputs((prev) => {
        const newApprovers = [...prev.approvers];

        newApprovers[index] = {
          userId: selectedData.userId || "M000",
          category: selectedData.category || "SALES",
          name: selectedData.name,
        };

        // Check if actually changed
        const hasChanged =
          JSON.stringify(prev.approvers) !== JSON.stringify(newApprovers);

        if (!hasChanged) {
          return prev; // No change, don't re-render
        }

        return { ...prev, approvers: newApprovers };
      });
    },
    // eslint-disable-next-line
    [priceViolations, formInputs.privilegeCase], //  Add currentUserId to dependencies
  );

  //  ADD THIS — stable reference, won't recreate on every render
  const handleApproverFirstIndexSelection = useCallback(
    (data) => handleApproverSelection(0, data),
    [handleApproverSelection], // handleApproverSelection is already useCallback'd
  );

  const hideFailureModal = () => setShowFailureModal(false);
  // eslint-disable-next-line
  const handleERFSelection = (erfData) => {
    setSelectedERF(erfData);
    setIsERFLoaded(true);

    // Auto-fill all patient details
    setFormInputs((prev) => ({
      ...prev,

      // Patient Details
      patientName: erfData.patientName || "",
      mobileNr: erfData.mobileNr || "",
      hospitalName: erfData.hospitalName || "",
      AssociateNumber: erfData.AssociateNumber || "",
      email: erfData.email || "",
      address: erfData.address || "",
      address2: erfData.address2 || "",
      city: erfData.city || "",
      age: erfData.age || "",
      weight: erfData.weight || "",
      gender: erfData.patientGender || "",
      gstNo: erfData.gstNo || "",
      panNo: erfData.panNo || "",
      isDiabetic: erfData.isDiabetic || "No",
      aadharNo: erfData.aadharNo || "",
      patientChallenges: erfData.patientChallenges || "",

      // Caregiver Details
      caregiverName: erfData.caregiverName || "",
      caregiverMobileNr: erfData.caregiverMobileNr || "",
      caregiverRelation: erfData.caregiverRelation || "",
      idProof: erfData.idProof || "Select",
      idProofNumber: erfData.idProofNumber || "",
      emailId: erfData.emailId || "",

      // Billing Address
      billingAddress: erfData.billingAddress || "",
      billingState: erfData.billingState || "Select",
      billingPinCode: erfData.billingPinCode || "",

      // Delivery Address
      deliveryAddress: erfData.deliveryAddress || "",
      deliveryState: erfData.deliveryState || "Select",
      deliveryPinCode: erfData.deliveryPinCode || "",

      // Doctor Details (optional)
      doctorName: erfData.doctorName || "",
      drType: erfData.drType || "Select",
      drCode: erfData.drCode || "",
      // hospital: erfData.hospital || "",
      patientErfHospitalName:
        erfData.patientErfHospitalName || erfData.hospital || "",
    }));

    // Clear validation errors
    setValidation({});
  };

  const handleGetSelectedMaterial = (selectedData) => {
    if (!Array.isArray(selectedData) || selectedData.length === 0) return;

    const processItems = (items) =>
      items
        .map((item, index) => {
          const qty = 1;
          const taxPercentage = parseFloat(item.taxCode) || 0;

          // // ── RENT LOGIC ────────────────────────────────────────────────────
          // if (activeItemType === "Rent") {
          //   const RENT_TAX_RATE = 18;
          //   const days = Math.max(globalRentConfig.days, 1);
          //   const rentPerDay = parseFloat(item.rentPerDayPrice) || 0; // net rate/day

          //   // FORWARD: Net = (net rate/day) × days × qty
          //   const netVal = parseFloat((qty * rentPerDay * days).toFixed(2));
          //   const taxAmt = parseFloat(
          //     (netVal * (RENT_TAX_RATE / 100)).toFixed(2),
          //   );
          //   const grossVal = parseFloat((netVal + taxAmt).toFixed(2));
          if (activeItemType === "Rent") {
            const RENT_TAX_RATE = 18;
            const days = Math.max(globalRentConfig.days, 1);
            const rentSlab = globalRentConfig.rentSlab;

            // Determine net slab amount per unit based on selected slab
            let masterNetRateForSlab = 0;
            switch (rentSlab) {
              case "Per Day":
                masterNetRateForSlab =
                  (parseFloat(item.rentPerDayPrice) || 0) * days;
                break;
              case "7 Days":
                masterNetRateForSlab = parseFloat(item.rent7DaysPrice) || 0;
                break;
              case "15 Days":
                masterNetRateForSlab = parseFloat(item.rent15DaysPrice) || 0;
                break;
              case "30 Days":
                masterNetRateForSlab = parseFloat(item.rent30DaysPrice) || 0;
                break;
              default:
                masterNetRateForSlab =
                  (parseFloat(item.rentPerDayPrice) || 0) * days;
            }

            // Net rate/day for display
            const ratePerDay =
              rentSlab === "Per Day"
                ? parseFloat(item.rentPerDayPrice) || 0
                : days > 0
                  ? masterNetRateForSlab / days
                  : 0;

            const netVal = parseFloat((qty * masterNetRateForSlab).toFixed(2));
            const taxAmt = parseFloat(
              (netVal * (RENT_TAX_RATE / 100)).toFixed(2),
            );
            const grossVal = parseFloat((netVal + taxAmt).toFixed(2));

            return {
              local_id: `${Date.now()}-${index}`,
              materialCode: item?.materialCode,
              materialCategory: item?.materialCategory || "",
              hsnCode: item?.hsnCode || "",
              materialDescription: item?.materialDescription,
              materialGroup: item?.materialGroup || "",
              materialSubCategory: item?.materialSubCategory || "",
              brand: item?.brand || "",
              masterTaxPercent: parseFloat(item?.taxCode) || 0,
              godown: item?.godown || "",
              barcode: null,
              // masterMRP / masterSellingPrice are now net values from the DB
              masterMRP: parseFloat(item.mrPrice) || 0,
              masterSellingPrice: parseFloat(item.salePrice) || 0,
              masterUnitPrice: parseFloat(item.unitPrice) || 0,
              itemType: activeItemType,
              orderedQuantity: qty,
              // sellingPrice: parseFloat(rentPerDay.toFixed(4)), // net rate/day
              // rentPerDay: rentPerDay,
              sellingPrice: parseFloat(ratePerDay.toFixed(4)),
              rentPerDay: parseFloat(item.rentPerDayPrice) || 0,
              rent7Days: parseFloat(item.rent7DaysPrice) || 0, // net slab amount
              rent15Days: parseFloat(item.rent15DaysPrice) || 0,
              rent30Days: parseFloat(item.rent30DaysPrice) || 0,
              rentSlab: globalRentConfig.rentSlab,
              rentSlabCode: globalRentConfig.rentSlabCode,
              rentSlabDescription: globalRentConfig.rentSlabDescription,
              fromDate: globalRentConfig.fromDate,
              toDate: globalRentConfig.toDate,
              days: globalRentConfig.days,
              netValue: netVal,
              grossValue: grossVal,
              taxPercentage: RENT_TAX_RATE,
              taxAmount: taxAmt,
              actualSecurityAmt: isHospital
                ? 0
                : parseFloat(item.securityAmt) || 0,
              securityAmt: isHospital ? 0 : parseFloat(item.securityAmt) || 0,
              refundAmt: 0,
              refundDays: 0,
              orderUnitOfMeasure: item.unit || "EA",
              storageLocn: "Main Store",
              currencyCode: "INR",
              accessoriesCode: item?.accessoriesCode || [],
              accessories: item?.accessories || false,
              extensionStatus: "Open",
            };
          }

          // ── NURSING ───────────────────────────────────────────────────────────────────
          if (activeItemType === "Nursing") {
            const days = Math.max(globalRentConfig.days, 1);
            const rentSlab = globalRentConfig.rentSlab;

            // Determine net slab amount per unit
            let netPerUnit = 0;
            switch (rentSlab) {
              case "Per Day":
                netPerUnit = (parseFloat(item.rentPerDayPrice) || 0) * days;
                break;
              case "7 Days":
                netPerUnit = parseFloat(item.rent7DaysPrice) || 0;
                break;
              case "15 Days":
                netPerUnit = parseFloat(item.rent15DaysPrice) || 0;
                break;
              case "30 Days":
                netPerUnit = parseFloat(item.rent30DaysPrice) || 0;
                break;
              default:
                netPerUnit = (parseFloat(item.rentPerDayPrice) || 0) * days;
            }

            const netVal = parseFloat((qty * netPerUnit).toFixed(2));
            const taxAmt = parseFloat(
              (netVal * (taxPercentage / 100)).toFixed(2),
            );
            const grossVal = parseFloat((netVal + taxAmt).toFixed(2));

            // Net rate/day (for the Rate/Day display column)
            const ratePerDay =
              rentSlab === "Per Day"
                ? parseFloat(item.rentPerDayPrice) || 0
                : days > 0
                  ? netPerUnit / days
                  : 0;

            return {
              local_id: `${Date.now()}-${index}`,
              materialCode: item?.materialCode,
              materialCategory: item?.materialCategory || "",
              hsnCode: item?.hsnCode || "",
              materialDescription: item?.materialDescription,
              materialGroup: item?.materialGroup || "",
              materialSubCategory: item?.materialSubCategory || "",
              brand: item?.brand || "",
              masterTaxPercent: parseFloat(item?.taxCode) || 0,
              godown: item?.godown || "",
              barcode: null,
              masterMRP: parseFloat(item.mrPrice) || 0,
              masterSellingPrice: parseFloat(item.salePrice) || 0,
              masterUnitPrice: parseFloat(item.unitPrice) || 0,
              itemType: "Nursing",
              orderedQuantity: qty,
              sellingPrice: parseFloat(ratePerDay.toFixed(4)), // net rate/day
              rentPerDay: parseFloat(item.rentPerDayPrice) || 0,
              rent7Days: parseFloat(item.rent7DaysPrice) || 0,
              rent15Days: parseFloat(item.rent15DaysPrice) || 0,
              rent30Days: parseFloat(item.rent30DaysPrice) || 0,
              rentSlab: globalRentConfig.rentSlab,
              rentSlabCode: globalRentConfig.rentSlabCode,
              rentSlabDescription: globalRentConfig.rentSlabDescription,
              fromDate: globalRentConfig.fromDate,
              toDate: globalRentConfig.toDate,
              days: globalRentConfig.days,
              netValue: netVal,
              grossValue: grossVal,
              taxPercentage: taxPercentage,
              taxAmount: taxAmt,
              actualSecurityAmt: 0,
              securityAmt: 0,
              refundAmt: 0,
              refundDays: 0,
              orderUnitOfMeasure: item.unit || "EA",
              storageLocn: "Main Store",
              currencyCode: "INR",
              accessoriesCode: item?.accessoriesCode || [],
              accessories: item?.accessories || false,
              extensionStatus: "Open",
            };
          }

          // ── SALE ──────────────────────────────────────────────────────────────────────
          if (activeItemType === "Sale") {
            const netUnitPrice = parseFloat(item.salePrice) || 0; // net unit price from master

            // FORWARD: Net = qty × net unit price
            const netVal = parseFloat((qty * netUnitPrice).toFixed(2));
            const taxAmt = parseFloat(
              (netVal * (taxPercentage / 100)).toFixed(2),
            );
            const grossVal = parseFloat((netVal + taxAmt).toFixed(2));

            return {
              local_id: `${Date.now()}-${index}`,
              materialCode: item?.materialCode,
              materialCategory: item?.materialCategory || "",
              hsnCode: item?.hsnCode || "",
              materialDescription: item?.materialDescription,
              godown: item?.godown || "",
              materialGroup: item?.materialGroup || "",
              materialSubCategory: item?.materialSubCategory || "",
              brand: item?.brand || "",
              masterTaxPercent: parseFloat(item?.taxCode) || 0,
              barcode: null,
              masterMRP: parseFloat(item.mrPrice) || 0, // net
              masterSellingPrice: parseFloat(item.salePrice) || 0, // net
              masterUnitPrice: parseFloat(item.unitPrice) || 0,
              itemType: "Sale",
              orderedQuantity: qty,
              sellingPrice: netUnitPrice, // net unit price
              rentPerDay: parseFloat(item.rentPerDayPrice) || 0,
              rent7Days: parseFloat(item.rent7DaysPrice) || 0,
              rent15Days: parseFloat(item.rent15DaysPrice) || 0,
              rent30Days: parseFloat(item.rent30DaysPrice) || 0,
              fromDate: getTodaysData(),
              toDate: "",
              days: 0,
              netValue: netVal,
              grossValue: grossVal,
              taxPercentage: taxPercentage,
              taxAmount: taxAmt,
              actualSecurityAmt: 0,
              securityAmt: 0,
              refundAmt: 0,
              refundDays: 0,
              orderUnitOfMeasure: item.unit || "EA",
              storageLocn: "Main Store",
              currencyCode: "INR",
              accessoriesCode: item?.accessoriesCode || [],
              accessories: item?.accessories || false,
            };
          }

          // ── SERVICE ───────────────────────────────────────────────────────────────────
          if (activeItemType === "Service") {
            const netUnitPrice = parseFloat(item.salePrice) || 0; // net unit price

            const netVal = parseFloat((qty * netUnitPrice).toFixed(2));
            const taxAmt = parseFloat(
              (netVal * (taxPercentage / 100)).toFixed(2),
            );
            const grossVal = parseFloat((netVal + taxAmt).toFixed(2));

            return {
              local_id: `${Date.now()}-${index}`,
              materialCode: item?.materialCode,
              materialCategory: item?.materialCategory || "",
              hsnCode: item?.hsnCode || "",
              materialDescription: item?.materialDescription,
              godown: item?.godown || "",
              materialGroup: item?.materialGroup || "",
              materialSubCategory: item?.materialSubCategory || "",
              brand: item?.brand || "",
              masterTaxPercent: parseFloat(item?.taxCode) || 0,
              barcode: "",
              masterMRP: parseFloat(item.mrPrice) || 0,
              masterSellingPrice: parseFloat(item.salePrice) || 0,
              masterUnitPrice: parseFloat(item.unitPrice) || 0,
              itemType: "Service",
              orderedQuantity: qty,
              sellingPrice: netUnitPrice, // net unit price
              rentPerDay: 0,
              rent7Days: 0,
              rent15Days: 0,
              rent30Days: 0,
              fromDate: getTodaysData(),
              toDate: "",
              days: 0,
              netValue: netVal,
              grossValue: grossVal,
              taxPercentage: taxPercentage,
              taxAmount: taxAmt,
              actualSecurityAmt: 0,
              securityAmt: 0,
              refundAmt: 0,
              refundDays: 0,
              orderUnitOfMeasure: item.unit || "EA",
              storageLocn: "Main Store",
              currencyCode: "INR",
              accessoriesCode: item?.accessoriesCode || [],
              accessories: item?.accessories || false,
            };
          }

          // console.warn(`Unknown activeItemType: ${activeItemType}`);
          return null;
        })
        .filter(Boolean); //  Filter out any null values

    if (activeItemType === "Rent") {
      setRentItems((prev) => [...prev, ...processItems(selectedData)]);
    } else if (activeItemType === "Nursing") {
      setNursingItems((prev) => [...prev, ...processItems(selectedData)]);
    } else {
      setSaleItems((prev) => [...prev, ...processItems(selectedData)]);
    }
    setModalShow(false);
  };

  const addProductDetails = (type) => {
    setActiveItemType(type || formInputs.erfType);
    setActiveItemTypeCode(formInputs.erfTypeCode);
    setModalShow(true);
  };

  // Fetch All Hospitals
  const fetchHospitalList = async () => {
    setIsLoadingHospitals(true);
    try {
      let payload;

      if (isSalesUser) {
        payload = {
          queryName: "Get_All_Hospital_Names",
          module: "",
          page: 1,
          per_page: 1000,
          timeZone: "Asia/Calcutta",
          rowFilter: {
            $and: [{ salesOfficerCode: currentUserId }], // ← filtered
          },
        };
      } else {
        payload = {
          queryName: "Get_All_Hospital_Names",
          module: "",
          page: 1,
          per_page: 1000,
          timeZone: "Asia/Calcutta",
        };
      }

      const response = await ItemService.generateQuery(payload);

      if (response?.status === 200 && Array.isArray(response.data)) {
        setHospitalList(response.data);
      } else {
        setHospitalList([]);
      }
    } catch (err) {
      console.error("Error fetching hospitals:", err);
      toast.error("Failed to load hospital list");
      setHospitalList([]);
    } finally {
      setIsLoadingHospitals(false);
    }
  };

  // Fetch All Doctors (for "Doctor" type)
  const fetchAllDoctors = async () => {
    setIsLoadingDoctors(true);
    try {
      // const payload = {
      //   queryName: "Get_All_Doctors",
      //   module: "",
      //   page: 1,
      //   per_page: 1000, // Get all doctors
      //   timeZone: "Asia/Calcutta",
      // };

      let payload;

      if (isSalesUser) {
        // Sales person: filter by their salesOfficerCode
        payload = {
          queryName: "Get_All_Doctors",
          module: "",
          page: 1,
          per_page: 1000,
          timeZone: "Asia/Calcutta",
          rowFilter: {
            $and: [{ salesOfficerCode: currentUserId }], // ← filtered
          },
        };
      } else {
        // Admin / PCD / others: get all
        payload = {
          queryName: "Get_All_Doctors",
          module: "",
          page: 1,
          per_page: 1000,
          timeZone: "Asia/Calcutta",
        };
      }

      const response = await ItemService.generateQuery(payload);

      if (response?.status === 200 && Array.isArray(response.data)) {
        // Format for dropdown
        const formattedDoctors = response.data.map((doc) => ({
          id: doc.userId,
          name: doc.doctorName,
          code: doc.userId,
          userId: doc.userId,
          doctorName: doc.doctorName,
          hospitalName: doc.hospitalName || doc.doctorHospitalName,
          description: `${doc.doctorName} - ${doc.hospitalName || doc.doctorHospitalName}`,
          ...doc, // Keep all original data
        }));

        setDoctorsList(formattedDoctors);
      } else {
        setDoctorsList([]);
      }
    } catch (err) {
      console.error("Error fetching doctors:", err);
      toast.error("Failed to load doctors list");
      setDoctorsList([]);
    } finally {
      setIsLoadingDoctors(false);
    }
  };

  // Fetch Doctors by Hospital
  const fetchDoctorsByHospital = async (hospitalName) => {
    setIsLoadingDoctors(true);
    try {
      const andFilters = [{ hospitalName }];

      if (isSalesUser) {
        andFilters.push({ salesOfficerCode: currentUserId }); // ← add filter for sales
      }

      const payload = {
        queryName: "Get_All_Doctors",
        module: "",
        page: 1,
        per_page: 1000,
        timeZone: "Asia/Calcutta",
        rowFilter: { $and: andFilters },
      };

      const response = await ItemService.generateQuery(payload);

      if (response?.status === 200 && Array.isArray(response.data)) {
        // Format for dropdown
        const formattedDoctors = response.data.map((doc) => ({
          id: doc.userId,
          name: doc.doctorName,
          code: doc.userId,
          userId: doc.userId,
          doctorName: doc.doctorName,
          hospitalName: doc.hospitalName || doc.doctorHospitalName,
          description: `${doc.doctorName} (${doc.userId})`,
          ...doc,
        }));

        setDoctorsList(formattedDoctors);
      } else {
        setDoctorsList([]);
        toast.info(`No doctors found for ${hospitalName}`);
      }
    } catch (err) {
      console.error("Error fetching doctors by hospital:", err);
      toast.error("Failed to load doctors for selected hospital");
      setDoctorsList([]);
    } finally {
      setIsLoadingDoctors(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchCompanyList = async () => {
      setIsLoadingCompany(true);
      try {
        const payload = {
          queryName: "Get_CompanyMaster_List",
          module: "",
          page: 1,
          per_page: 200,
          timeZone: "Asia/Calcutta",
        };

        const response = await ItemService.generateCustomerQuery(payload);

        if (
          isMounted &&
          response?.status === 200 &&
          Array.isArray(response.data)
        ) {
          const formatted = response.data.map((company) => ({
            id: company.companyNr || company.documentNumber,
            name: company.name || company.organisationName,
            companyNr: company.companyNr,
            companyCode: company.companyCode,
            companyName: company.companyCodeDesc || company.organisationName,
            organisationName: company.organisationName,
          }));
          setCompanyList(formatted);

          const medirent = formatted.find(
            (company) => company.name === "Medirent",
          );

          if (medirent) {
            // Set the selected company state
            setSelectedCompany(medirent);

            // Update formInputs with company details
            setFormInputs((prev) => ({
              ...prev,
              companyName: medirent.companyName,
              companyOrganisation: medirent.organisationName,
              companyNr: medirent.companyNr,
              companyCode: medirent.companyCode,
            }));
          }
        }
      } catch (err) {
        console.error("Error fetching company list:", err);
        if (isMounted) toast.error("Failed to load company list");
      } finally {
        if (isMounted) setIsLoadingCompany(false);
      }
    };

    fetchCompanyList();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadHospitals = async () => {
      if (erfForType === "Hospital") {
        setErfIsLoadingHospitals(true);

        try {
          const payload = {
            queryName: "getSupplierList",
            module: "",
            page: 1,
            per_page: 1000,
            timeZone: "Asia/Calcutta",
            rowFilter: {
              $and: [
                {
                  "vendorCategory.vendorCategoryCode": "V04",
                },
              ],
            },
          };

          const response = await ItemService.generateQuery(payload);

          if (
            isMounted &&
            response?.status === 200 &&
            Array.isArray(response.data)
          ) {
            const formattedHospitals = response.data.map((hospital) => ({
              id: hospital.userId || hospital._id,
              name: hospital.hospitalName || hospital.organisationName,
              userId: hospital.userId,
              hospitalName: hospital.hospitalName,
              organisationName: hospital.organisationName,
              AssociateNumber: hospital.AssociateNumber,
              mobileNr: hospital.mobileNr,
              email: hospital.email,
              address: hospital.address,
              address2: hospital.address2,
              city: hospital.city,
              state: hospital.state,
              cityPostalCode: hospital.cityPostalCode,

              certifications: hospital.certifications || [],
            }));

            setErfHospitalList(formattedHospitals);
          } else if (isMounted) {
            setErfHospitalList([]);
          }
        } catch (err) {
          console.error("Error fetching ERF hospitals:", err);
          if (isMounted) {
            toast.error("Failed to load ERF hospital list");
            setErfHospitalList([]);
          }
        } finally {
          if (isMounted) {
            setErfIsLoadingHospitals(false);
          }
        }
      } else {
        if (isMounted) {
          setErfHospitalList([]);
        }
      }
    };

    loadHospitals();

    return () => {
      isMounted = false;
    };
  }, [erfForType]);

  useEffect(() => {
    let isMounted = true;

    const loadNurse = async () => {
      if (formInputs.erfType === "Nursing Service") {
        setIsLoadingErfNurse(true);

        try {
          const payload = {
            queryName: "getSupplierList",
            module: "",
            page: 1,
            per_page: 1000,
            timeZone: "Asia/Calcutta",
            rowFilter: {
              $and: [
                {
                  "vendorCategory.vendorCategoryCode": "V13",
                },
              ],
            },
          };

          const response = await ItemService.generateQuery(payload);

          if (
            isMounted &&
            response?.status === 200 &&
            Array.isArray(response.data)
          ) {
            const formattedNurse = response.data.map((nurse) => ({
              id: nurse.userId || nurse._id,
              name: nurse.hospitalName || nurse.organisationName,
              userId: nurse.userId,
              hospitalName: nurse.hospitalName,
              organisationName: nurse.organisationName,
              AssociateNumber: nurse.AssociateNumber,
              mobileNr: nurse.mobileNr,
              email: nurse.email,
              address: nurse.address,
              address2: nurse.address2,
              city: nurse.city,
              state: nurse.state,
              cityPostalCode: nurse.cityPostalCode,
              serviceProviderCode: nurse.serviceProviderCode,
              serviceProviderName: nurse.serviceProviderName,

              certifications: nurse.certifications || [],
            }));

            setErfNurseList(formattedNurse);
          } else if (isMounted) {
            setErfNurseList([]);
          }
        } catch (err) {
          console.error("Error fetching ERF hospitals:", err);
          if (isMounted) {
            toast.error("Failed to load ERF hospital list");
            setErfNurseList([]);
          }
        } finally {
          if (isMounted) {
            setIsLoadingErfNurse(false);
          }
        }
      } else {
        if (isMounted) {
          setErfNurseList([]);
        }
      }
    };

    loadNurse();

    return () => {
      isMounted = false;
    };
  }, [formInputs.erfType]);

  // Fetch All Hospitals
  const fetchPatientList = async () => {
    setIsLoadingPatient(true);
    try {
      let payload;
      if (adminUser) {
        payload = {
          queryName: "getSupplierList",
          module: "",
          page: 1,
          per_page: 10000,
          timeZone: "Asia/Calcutta",
          rowFilter: {
            $and: [
              {
                "vendorCategory.vendorCategoryCode": "V12", // Patient category
              },
            ],
          },
        };
      } else {
        const salesOfficerCode = currentUserId;

        if (!salesOfficerCode) {
          console.error(
            "salesOfficerCode not found — patient list will be empty",
          );
          setPatientList([]);
          return;
        }
        payload = {
          queryName: "GetPatientsBySalesOfficer",
          salesOfficerCode: salesOfficerCode,
          page: 1,
          per_page: 10000,
          timeZone: "Asia/Calcutta",
        };
      }

      const response = await ItemService.generateQuery(payload);

      if (response?.status === 200 && Array.isArray(response.data)) {
        // Format patient data for dropdown
        const formattedPatients = response.data.map((patient) => ({
          id: patient.userId,
          name: patient.employeeName, // Coming from backend with this key employeeName
          userId: patient.userId,

          hospitalName: patient.hospitalName,
          organisationName: patient.organisationName,
          AssociateNumber: patient.AssociateNumber,
          mobileNr: patient.mobileNr,
          email: patient.email,
          address: patient.address,
          address2: patient.address2,
          city: patient.city,
          state: patient.state,
          cityPostalCode: patient.cityPostalCode,

          age: patient?.age,
          weight: patient?.weight,
          patientGender: patient?.patientGender,
          aadharNo: patient.aadharNo,
          panNo: patient.panNo,
          patientChallenges: patient.patientChallenges,

          caregiverName: patient.caregiverName,
          caregiverMobileNr: patient.caregiverMobileNr,
          emailId: patient.emailId,
          caregiverRelation: patient.caregiverRelation,
          idProof: patient.idProof,
          idProofNumber: patient.idProofNumber,
        }));

        setPatientList(formattedPatients);
      } else {
        setPatientList([]);
      }
    } catch (err) {
      console.error("Error fetching patients:", err);
      toast.error("Failed to load patient list");
      setPatientList([]);
    } finally {
      setIsLoadingPatient(false);
    }
  };

  const fetchCostCenterList = async () => {
    setIsLoadingCostCenter(true);
    try {
      const payload = {
        queryName: "Get_Costcenter_List",
        module: "",
        page: 1,
        per_page: 1000,
        timeZone: "Asia/Calcutta",
        rowFilter: {
          $and: [
            {
              "vendorCategory.vendorCategoryCode": {
                $in: ["V04", "V15"],
              },
            },
          ],
        },
      };

      const response = await ItemService.generateQuery(payload);

      if (response?.status === 200 && Array.isArray(response.data)) {
        const formatted = response.data.map((person) => ({
          id: person.userId || person._id,
          name:
            person.firstName || person.employeeName || person.organisationName,
          userId: person.userId,
          code: person.userId, // used by costCenterCode
        }));
        setCostCenterList(formatted);
      } else {
        setCostCenterList([]);
      }
    } catch (err) {
      console.error("Error fetching cost center list:", err);
      toast.error("Failed to load cost center list");
      setCostCenterList([]);
    } finally {
      setIsLoadingCostCenter(false);
    }
  };

  const hideModal = () => setModalShow(false);

  const isHospital = useMemo(() => erfForType === "Hospital", [erfForType]);

  const handleSave = async () => {
    const validationErrors = {};
    let hasError = false;

    // ===================================================================
    // 1. B2B vs B2C Validation
    // ===================================================================
    const addError = (field, msg) => {
      validationErrors[field] = msg;
      hasError = true;
    };

    // ── Order Details ──
    if (
      !validateRequired(formInputs.salePoint) ||
      formInputs.salePoint === "Select"
    )
      addError("salePoint", "Sale Point is required");

    if (!validateRequired(formInputs.costCenter))
      addError("costCenter", "Cost Center is required");

    if (!validateRequired(formInputs.companyNr))
      addError("companyNr", "Company is required");

    if (!validateRequired(formInputs.billingName))
      addError("billingName", "Billing Name is required");

    if (!validateRequired(formInputs.expectedDeliveryDate))
      addError("expectedDeliveryDate", "Expected Delivery Date is required");

    if (!validateRequired(formInputs.expectedDeliveryTime))
      addError("expectedDeliveryTime", "Expected Delivery Time is required");

    // ── Doctor ──
    // if (!validateRequired(formInputs.drType) || formInputs.drType === "Select")
    //   addError("drType", "Doctor Type is required");

    // ── Patient ──
    if (!validateRequired(formInputs.patientName))
      addError("patientName", "Patient Name is required");

    if (!validateRequired(formInputs.mobileNr)) {
      addError("mobileNr", "Patient Mobile Number is required");
    } else if (!validateContactNumber(formInputs.mobileNr)) {
      addError("mobileNr", "Enter a valid 10-digit mobile number");
    }

    // if (!validateRequired(formInputs.aadharNo)) {
    //   addError("aadharNo", "Aadhar Number is required");
    // } else if (!validateAadhar(formInputs.aadharNo)) {
    //   addError("aadharNo", "Aadhar must be exactly 12 digits");
    // }

    if (formInputs.aadharNo && !validateAadhar(formInputs.aadharNo)) {
      addError("aadharNo", "Aadhar must be exactly 12 digits");
    }

    if (formInputs.panNo && !validatePAN(formInputs.panNo)) {
      addError("panNo", "PAN must be in format: AAAAA9999A");
    }

    // ID Proof Number (only required if ID Proof type is selected)
    // if (formInputs.idProof && formInputs.idProof !== "Select") {
    //   if (!validateRequired(formInputs.idProofNumber)) {
    //     addError("idProofNumber", `${formInputs.idProof} number is required`);
    //   }
    // }

    // ── Caregiver ──
    if (!validateRequired(formInputs.caregiverMobileNr)) {
      addError("caregiverMobileNr", "Caregiver Mobile Number is required");
    } else if (!validateContactNumber(formInputs.caregiverMobileNr)) {
      addError("caregiverMobileNr", "Enter a valid 10-digit caregiver number");
    }

    // ── Billing Address ──
    if (!validateRequired(formInputs.billingAddress))
      addError("billingAddress", "Billing Address is required");

    if (
      !validateRequired(formInputs.billingState) ||
      formInputs.billingState === "Select"
    )
      addError("billingState", "Billing State is required");

    if (!validateRequired(formInputs.billingPinCode)) {
      addError("billingPinCode", "Billing Pin Code is required");
    } else if (!validatePinCode(formInputs.billingPinCode)) {
      addError("billingPinCode", "Pin Code must be exactly 6 digits");
    }

    // ── Delivery Address ──
    if (!validateRequired(formInputs.deliveryAddress))
      addError("deliveryAddress", "Delivery Address is required");

    if (
      !validateRequired(formInputs.deliveryState) ||
      formInputs.deliveryState === "Select"
    )
      addError("deliveryState", "Delivery State is required");

    if (!validateRequired(formInputs.deliveryPinCode)) {
      addError("deliveryPinCode", "Delivery Pin Code is required");
    } else if (!validatePinCode(formInputs.deliveryPinCode)) {
      addError("deliveryPinCode", "Delivery Pin Code must be exactly 6 digits");
    }

    if (
      erfForType === "Hospital" &&
      (!selectedHospital || !selectedHospital.userId)
    ) {
      toast.error("Please select a hospital");
      return;
    }

    // ID Proof Validation (both B2B and B2C)
    // if (formInputs.idProof && formInputs.idProof !== "Select") {
    //   if (!validateRequired(formInputs.idProofNumber)) {
    //     validationErrors.idProofNumber = `Please enter ${formInputs.idProof} number`;
    //     hasError = true;
    //   }
    // }

    if (priceViolations.length > 0) {
      const approver = formInputs.approvers[0];
      if (!approver || !approver.userId) {
        toast.error(
          "Price violations detected. Please select an approver or correct the prices.",
        );
        setShowApprovalRequired(true);
        return;
      }
    }

    // ===================================================================
    // 3. Payment Mode Validation  ← ADD THIS ENTIRE BLOCK
    // ===================================================================

    if (!formInputs.paymentMode || formInputs.paymentMode === "Select") {
      toast.error("Please select a Payment Mode in Received Amount Breakup");
      return;
    }

    // Security mode required for Rent types
    // if (formInputs.erfType === "Rent" || formInputs.erfType === "Sale & Rent") {
    //   if (
    //     !formInputs.securityAmtMode ||
    //     formInputs.securityAmtMode === "Select"
    //   ) {
    //     toast.error("Please select a Security Amount Mode in Security Details");
    //     return;
    //   }
    //   if (!formInputs.securityMode || formInputs.securityMode === "Select") {
    //     toast.error(
    //       "Please select a Security Mode in Security Mode Would Be section",
    //     );
    //     return;
    //   }
    // }

    if (
      (formInputs.erfType === "Rent" || formInputs.erfType === "Sale & Rent") &&
      erfForType !== "Hospital"
    ) {
      if (
        !formInputs.securityAmtMode ||
        formInputs.securityAmtMode === "Select"
      ) {
        toast.error("Please select a Security Amount Mode in Security Details");
        return;
      }
      if (!formInputs.securityMode || formInputs.securityMode === "Select") {
        toast.error(
          "Please select a Security Mode in Security Mode Would Be section",
        );
        return;
      }
    }

    // ===================================================================
    // 4. ERF Type Specific Item Validation
    // ===================================================================
    switch (formInputs.erfType) {
      case "Sale":
        if (saleItems.length === 0) {
          toast.error("Please add at least one Sale product.");
          return;
        }
        break;
      case "Rent":
        if (rentItems.length === 0) {
          toast.error("Please add at least one Rent product.");
          return;
        }
        break;
      // case ERF_TYPE_CODES.SALE_AND_RENT:
      case "Sale & Rent":
        if (rentItems.length === 0) {
          toast.error("Please add at least one Rent product.");
          return;
        }
        if (saleItems.length === 0) {
          toast.error("Please add at least one Sale product.");
          return;
        }
        break;
      case "Service":
        if (saleItems.length === 0) {
          toast.error("Please add at least one Service item.");
          return;
        }
        break;
      case "Nursing Service":
        if (nursingItems.length === 0) {
          // validation
          toast.error("Please add at least one Nursing item.");
          return;
        }
        break;
      default:
        toast.error("Please select a valid ERF Type.");
        return;
    }

    // ===================================================================
    // 5. Quantity & Price Validation
    // ===================================================================
    const finalItems = [...rentItems, ...saleItems, ...nursingItems];
    const invalidQty = finalItems.filter(
      (item) => (parseFloat(item.orderedQuantity) || 0) < 1,
    );

    if (invalidQty.length > 0) {
      toast.error("Quantity must be at least 1 for all products");
      return;
    }

    const privilegeSettings =
      PRIVILEGE_SETTINGS[formInputs.privilegeCase] ||
      DEFAULT_PRIVILEGE_SETTINGS;

    if (!privilegeSettings.allowSellBelowLRP) {
      const invalidSaleItems = saleItems.filter(
        (item) =>
          parseFloat(item.sellingPrice) < parseFloat(item.masterSellingPrice),
      );
      if (invalidSaleItems.length > 0) {
        toast.error(
          "Sale/Service Unit Price cannot be less than Master Selling Price for this Privilege Case",
        );
        return;
      }
    }

    // ===================================================================
    // 6. Show Validation Errors if Any
    // ===================================================================
    setValidation((prev) => ({ ...prev, ...validationErrors }));

    // if (hasError) {
    //   setErrorFields(Object.keys(validationErrors));
    //   setShowFailureModal(true);
    //   return;
    // }

    if (hasError) {
      const fieldLabelMap = {
        salePoint: "Sale Point",
        costCenter: "Cost Center",
        billingName: "Billing Name",
        expectedDeliveryDate: "Expected Delivery Date",
        expectedDeliveryTime: "Expected Delivery Time",
        drType: "Doctor Type",
        companyNr: "Company",
        patientName: "Patient Name",
        mobileNr: "Patient Mobile Number",
        // aadharNo: "Aadhar Number",
        // panNo: "PAN Number",
        // idProofNumber: "ID Proof Number",
        caregiverMobileNr: "Caregiver Mobile Number",
        billingAddress: "Billing Address",
        billingState: "Billing State",
        billingPinCode: "Billing Pin Code",
        deliveryAddress: "Delivery Address",
        deliveryState: "Delivery State",
        deliveryPinCode: "Delivery Pin Code",
      };
      setErrorFields(
        Object.keys(validationErrors).map((key) => fieldLabelMap[key] || key),
      );
      setShowFailureModal(true);
      return;
    }

    // ===================================================================
    // 7. Construct Payload and Save
    // ===================================================================
    setLoading(true);
    try {
      // Determine if workflow approval is needed
      // const triggerWorkflow =
      //   APPROVAL_REQUIRED_CASES.includes(formInputs.privilegeCase) ||
      //   priceViolations.length > 0;
      const triggerWorkflow = priceViolations.length > 0;

      // Convert items to numbers
      const processedRentItems = rentItems.map((item) =>
        convertToNumbers(item),
      );
      const processedSaleItems = saleItems.map((item) =>
        convertToNumbers(item),
      );
      const processedNursingItems = nursingItems.map((item) =>
        convertToNumbers(item),
      );

      const selectedErfTypeObj = getErfTypeByName(formInputs.erfType);

      const payloadData = {
        ...formInputs,

        // ERF Type Information (Send both code and name)
        erfType: formInputs.erfType, // CODE: "RE", "SL", etc.
        erfTypeCode: formInputs.erfTypeCode,
        erfTypeName: selectedErfTypeObj?.name || formInputs.erfType,
        erfTypeDescription: formInputs.erfTypeDescription,

        companyName: formInputs.companyName,
        companyOrganisation: formInputs.companyOrganisation,
        companyNr: formInputs.companyNr,
        companyCode: formInputs.companyCode,

        drType: formInputs.drType,
        drListedType: formInputs.drListedType || "",
        drCode: formInputs.drCode,
        doctorName: formInputs.doctorName,
        patientErfHospitalName: formInputs.patientErfHospitalName,

        // ERF For Type (B2B or B2C)
        erfForType: erfForType, // CODE: "ERFP" or "ERFH"
        erfForTypeName: erfForType === "Patient" ? "Patient" : "Hospital",
        erfForTypeCode: erfForType === "Patient" ? "PAT" : "HSP",

        costCenter: formInputs.costCenter,
        erfCostCenterSubGroup: formInputs.erfCostCenterSubGroup || "SALESTEAM",

        hospitalName: formInputs.hospitalName,
        hospitalAssociateNumber: formInputs.hospitalAssociateNumber,
        hospitalMobileNr: formInputs.hospitalMobileNr,
        hospitalEmail: formInputs.hospitalEmail,
        hospitalGstNo: formInputs.hospitalGstNo,
        hospitalAddress: formInputs.hospitalAddress,
        hospitalAddress2: formInputs.hospitalAddress2,
        hospitalCity: formInputs.hospitalCity,
        hospitalState: formInputs.hospitalState,
        hospitalPinCode: formInputs.hospitalPinCode,
        hospitalBillingAddress: formInputs.hospitalBillingAddress,

        // Privilege Case (Send both code and name)
        privilegeCase: formInputs.privilegeCase, // CODE: "SC", "DC", etc.
        privilegeCaseCode: formInputs.privilegeCaseCode,
        privilegeCaseName: formInputs.privilegeCaseName,

        // Payment Mode (Send both)
        paymentMode: formInputs.paymentMode, // CODE
        paymentModeCode: formInputs.paymentModeCode,
        paymentModeName: formInputs.paymentModeName,

        // Security Modes (Send both)
        // securityAmtMode: formInputs.securityAmtMode,
        // securityAmtModeCode: formInputs.securityAmtModeCode,
        // securityAmtModeName: formInputs.securityAmtModeName,

        // securityMode: formInputs.securityMode,
        // securityModeCode: formInputs.securityModeCode,
        // securityModeName: formInputs.securityModeName,

        securityAmtMode: isHospital ? "" : formInputs.securityAmtMode,
        securityAmtModeCode: isHospital ? "" : formInputs.securityAmtModeCode,
        securityAmtModeName: isHospital ? "" : formInputs.securityAmtModeName,

        securityMode: isHospital ? "" : formInputs.securityMode,
        securityModeCode: isHospital ? "" : formInputs.securityModeCode,
        securityModeName: isHospital ? "" : formInputs.securityModeName,

        // ERF Dates (for Rent/Sale&Rent)
        ...((formInputs.erfType === "Rent" ||
          formInputs.erfType === "Sale & Rent" ||
          formInputs.erfType === "Nursing Service") && {
          erfStartDate: globalRentConfig.fromDate,
          erfEndDate: globalRentConfig.toDate,
          erfReminderDate: calculateReminderDate(
            globalRentConfig.toDate,
            globalRentConfig.days,
          ),
          erfReminderDaysBeforeEnd: REMINDER_DAYS_BEFORE_END,
          pcCallStatus: "",
          pcCallStatusCode: "",
          extensionLifecycleStatus: "ACTIVE", //"ACTIVE", "EXTENDED", "CLOSED"
          childExtensionNr: null,
        }),

        // Approval Process
        levelOfApproval: "1",
        approverList: formInputs.approvers.slice(0, 1),

        triggerWorkflow: triggerWorkflow,
        // approvalReason:
        //   priceViolations.length > 0
        //     ? "Price Violation"
        //     : formInputs.privilegeCase,
        approvalReason: priceViolations.length > 0 ? "Price Violation" : "",

        approverUserId1:
          triggerWorkflow && formInputs.approvers[0]?.userId
            ? formInputs.approvers[0].userId
            : "",
        approverUsername1:
          triggerWorkflow && formInputs.approvers[0]?.name
            ? formInputs.approvers[0].name
            : "",

        // Explicitly clear level 2 fields
        approverUserId2: "",
        approverUsername2: "",

        // Price Violations
        // priceViolations: priceViolations,
        // hasPriceViolations: priceViolations.length > 0,

        // User Info
        createdBy: currentUserId,

        // Financial Fields (Convert to numbers)
        netAmount: parseFloat(netAmount),
        balanceAmount: parseFloat(balanceAmount),
        // balanceSecurityAmt: parseFloat(balanceSecurityAmt),
        balanceSecurityAmt: isHospital ? 0 : parseFloat(balanceSecurityAmt),
        cartage: parseFloat(formInputs.cartage) || 0,
        salesCartage: cartageCheck.salesCartage
          ? parseFloat(formInputs.cartage) || 0
          : 0,
        rentCartage: cartageCheck.rentCartage
          ? parseFloat(formInputs.cartage) || 0
          : 0,
        installationCharges: parseFloat(formInputs.installationCharges) || 0,
        receivedAmount: parseFloat(formInputs.receivedAmount) || 0,
        cashAmount: parseFloat(formInputs.cashAmount) || 0,
        // receivedSecurityAmt: parseFloat(formInputs.receivedSecurityAmt) || 0,
        // securityCashAmt: parseFloat(formInputs.securityCashAmt) || 0,
        // securityModeCash: parseFloat(formInputs.securityModeCash) || 0,
        receivedSecurityAmt: isHospital
          ? 0
          : parseFloat(formInputs.receivedSecurityAmt) || 0,
        securityCashAmt: isHospital
          ? 0
          : parseFloat(formInputs.securityCashAmt) || 0,
        securityModeCash: isHospital
          ? 0
          : parseFloat(formInputs.securityModeCash) || 0,
        age: formInputs.age ? parseFloat(formInputs.age) : undefined,
        weight: formInputs.weight ? parseFloat(formInputs.weight) : undefined,

        erfStatusCode: "ATS", //---ATS = Assign To Store (code)
        erfStatus: "Assign To Store",

        // ── Line Item Totals (Header Level — for Reports) ──────────────────
        // Rent Table Totals
        rentNetTotal: itemTypeTotals.rentNetTotal,
        rentTaxTotal: itemTypeTotals.rentTaxTotal,
        rentGrossTotal: itemTypeTotals.rentGrossTotal,
        // rentSecurityTotal: itemTypeTotals.rentSecurityTotal,
        rentSecurityTotal: isHospital ? 0 : itemTypeTotals.rentSecurityTotal,

        // Sale Table Totals
        saleNetTotal: itemTypeTotals.saleNetTotal,
        saleTaxTotal: itemTypeTotals.saleTaxTotal,
        saleGrossTotal: itemTypeTotals.saleGrossTotal,

        // Service Table Totals
        serviceNetTotal: itemTypeTotals.serviceNetTotal,
        serviceTaxTotal: itemTypeTotals.serviceTaxTotal,
        serviceGrossTotal: itemTypeTotals.serviceGrossTotal,

        // Nursing Table Totals
        nursingNetTotal: itemTypeTotals.nursingNetTotal,
        nursingTaxTotal: itemTypeTotals.nursingTaxTotal,
        nursingGrossTotal: itemTypeTotals.nursingGrossTotal,

        // Overall Line Item Grand Totals (all tables combined)
        lineItemNetTotal: itemTypeTotals.lineItemNetTotal,
        lineItemTaxTotal: itemTypeTotals.lineItemTaxTotal,
        lineItemGrossTotal: itemTypeTotals.lineItemGrossTotal,
        lineItemGrossRounded: itemTypeTotals.lineItemGrossRounded,
        roundOffAmount: itemTypeTotals.roundOffAmount,
        financeCollectionConfirmation: "Not Confirmed",
        tallyStatusIndicator: "Pending",
        tallyHistory: [],
      };

      // Specific Logic for Payload Item Keys
      if (formInputs.erfType === "Service") {
        payloadData.rentItems = [];
        payloadData.saleItems = [];
        payloadData.nursingItems = [];
        payloadData.erfStatus = "Assign To Store";
        payloadData.erfStatusCode = "ATS";
        payloadData.serviceItems = processedSaleItems;
      } else if (formInputs.erfType === "Nursing Service") {
        payloadData.rentItems = [];
        payloadData.saleItems = [];
        payloadData.serviceItems = [];
        payloadData.nursingItems = processedNursingItems;

        payloadData.nurseUserId = formInputs.nurseUserId;
        payloadData.nurseName = formInputs.nurseName;
        payloadData.nurseAssociateNumber = formInputs.nurseAssociateNumber;
        payloadData.nurseMobileNr = formInputs.nurseMobileNr;
        payloadData.nurseEmail = formInputs.nurseEmail;
        payloadData.nurseAddress = formInputs.nurseAddress;
        payloadData.nurseAddress2 = formInputs.nurseAddress2;
        payloadData.nurseCity = formInputs.nurseCity;

        payloadData.serviceProviderCode = formInputs.serviceProviderCode;
        payloadData.serviceProviderName = formInputs.serviceProviderName;

        payloadData.nursePanNo = formInputs.nursePanNo;
        payloadData.nurseBillingAddress = formInputs.nurseBillingAddress;
        payloadData.nurseBillingState = formInputs.nurseBillingState;
        payloadData.nurseBillingPinCode = formInputs.nurseBillingPinCode;
        payloadData.erfStatus = "Service";
        payloadData.erfStatusCode = "SERV";
      } else {
        payloadData.rentItems = processedRentItems;
        payloadData.saleItems = processedSaleItems;
        payloadData.serviceItems = [];
        payloadData.nursingItems = [];
      }

      const payload = {
        data: payloadData,
      };

      // console.log("Payload", payload);

      await ItemService.addEquipmentRentForm(payload);
      await fetchData();

      toast.success("ERF Created Successfully");
      navigate(Constants.CUSTOMER_V5_MR_ERF_LIST);
    } catch (err) {
      toast.error("Failed to create ERF");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async (page = 1, size = 10, search = "") => {
    setLoading(true);
    try {
      const payload = {
        queryName: "ERFList",
        page,
        per_page: size,
        searchTerm: search,
        timeZone: "Asia/Calcutta",
        rowFilter: {},
      };

      await dispatch(getEquipmentRentFormList(payload));
    } catch (error) {
      console.error("Error fetching list", error);
      toast.error("Error loading records");
    } finally {
      setLoading(false);
    }
  };

  // =============================================================================
  // TABLE COLUMNS CONFIG
  // =============================================================================

  //  compute privilegeSettings INSIDE useMemo
  const rentColumns = useMemo(() => {
    const privilegeSettings =
      PRIVILEGE_SETTINGS[formInputs.privilegeCase] ||
      DEFAULT_PRIVILEGE_SETTINGS;
    return getRentOnlyColumns(
      privilegeSettings,
      handleLineItemChange,
      removeItemRow,
      isHospital,
    );
  }, [
    formInputs.privilegeCase,
    handleLineItemChange,
    removeItemRow,
    isHospital,
  ]);

  const saleColumns = useMemo(() => {
    const privilegeSettings =
      PRIVILEGE_SETTINGS[formInputs.privilegeCase] ||
      DEFAULT_PRIVILEGE_SETTINGS;
    return getSaleOnlyColumns(
      privilegeSettings,
      handleLineItemChange,
      removeItemRow,
    );
  }, [formInputs.privilegeCase, handleLineItemChange, removeItemRow]);

  const serviceColumns = useMemo(() => {
    const privilegeSettings =
      PRIVILEGE_SETTINGS[formInputs.privilegeCase] ||
      DEFAULT_PRIVILEGE_SETTINGS;
    return getServiceOnlyColumns(
      privilegeSettings,
      handleLineItemChange,
      removeItemRow,
    );
  }, [formInputs.privilegeCase, handleLineItemChange, removeItemRow]);

  const nursingColumns = useMemo(() => {
    const privilegeSettings =
      PRIVILEGE_SETTINGS[formInputs.privilegeCase] ||
      DEFAULT_PRIVILEGE_SETTINGS;
    return getNursingOnlyColumns(
      privilegeSettings,
      handleLineItemChange,
      removeItemRow,
      isHospital,
    );
  }, [
    formInputs.privilegeCase,
    handleLineItemChange,
    removeItemRow,
    isHospital,
  ]);

  // ── Add these computed helpers right before the return() statement ──────────
  // These make the disable logic readable everywhere

  const isPaymentModeSelected =
    formInputs.paymentMode !== "Select" && formInputs.paymentMode !== "";

  const isSecurityAmtModeSelected =
    formInputs.securityAmtMode !== "Select" &&
    formInputs.securityAmtMode !== "";

  const paymentRules = PAYMENT_MODE_RULES[formInputs.paymentMode] || {};
  const securityAmtRules = PAYMENT_MODE_RULES[formInputs.securityAmtMode] || {};
  const privilegeSettings =
    PRIVILEGE_SETTINGS[formInputs.privilegeCase] || DEFAULT_PRIVILEGE_SETTINGS;

  //rent and sale chartage
  const [cartageCheck, setCartageCheck] = useState({
    salesCartage: false,
    rentCartage: false,
  });

  // Handles manual toggle — only reachable for SLRE (Sale & Rent) since others are disabled
  const handleCartageCheck = (e) => {
    const { name, checked } = e.target;

    // Mutually exclusive: checking one unchecks the other
    const newCartageCheck = {
      salesCartage: name === "salesCartage" ? checked : false,
      rentCartage: name === "rentCartage" ? checked : false,
    };

    setCartageCheck(newCartageCheck);

    // Read cartage from functional prev to avoid stale closure bug
    setFormInputs((prev) => ({
      ...prev,
      salesCartage: newCartageCheck.salesCartage
        ? Number(prev.cartage) || 0
        : 0,
      rentCartage: newCartageCheck.rentCartage ? Number(prev.cartage) || 0 : 0,
    }));
  };

  // Auto-set checkboxes whenever ERF Type changes
  useEffect(() => {
    if (!formInputs.erfTypeCode) return;

    switch (formInputs.erfTypeCode) {
      case "SL": // Sale → force salesCartage, lock both
        setCartageCheck({ salesCartage: true, rentCartage: false });
        setFormInputs((prev) => ({
          ...prev,
          salesCartage: Number(prev.cartage) || 0,
          rentCartage: 0,
        }));
        break;

      case "RE": // Rent → force rentCartage, lock both
        setCartageCheck({ salesCartage: false, rentCartage: true });
        setFormInputs((prev) => ({
          ...prev,
          salesCartage: 0,
          rentCartage: Number(prev.cartage) || 0,
        }));
        break;

      case "SLRE": // Sale & Rent → default to salesCartage, user can switch
        setCartageCheck({ salesCartage: true, rentCartage: false });
        setFormInputs((prev) => ({
          ...prev,
          salesCartage: Number(prev.cartage) || 0,
          rentCartage: 0,
        }));
        break;

      default: // Service, Nursing — clear both
        setCartageCheck({ salesCartage: false, rentCartage: false });
        setFormInputs((prev) => ({ ...prev, salesCartage: 0, rentCartage: 0 }));
        break;
    }
    // eslint-disable-next-line
  }, [formInputs.erfTypeCode]);

  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide quoation">
          <div className="rightSideInner PageCommonCSS">
            <h2 className="mainTitle">Create New Rental (ERF)</h2>
            <p className="subText">Enter details below</p>
            <hr />

            <Loader isLoading={loading} className={"text-center"} />

            {!loading && (
              <>
                {/* ORDER DETAILS ACCORDION */}
                <div className="accordion mb-4" id="accordionOrder">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <Row className="accordion-button">
                        <Col
                          xs={1}
                          md={1}
                          lg={1}
                          className="text-left arrowBtn"
                        >
                          <i
                            className={`bi ${
                              isCardVisible
                                ? "bi-chevron-down"
                                : "bi-chevron-right"
                            }`}
                            onClick={toggleCardVisibility}
                          ></i>
                        </Col>
                        <Col
                          xs={1}
                          md={1}
                          lg={1}
                          className="text-center logoBtn"
                        >
                          <span className="iconLogo">OD</span>
                        </Col>
                        <Col xs={10} md={6} lg={8}>
                          <span className="headerTitle">
                            Order & Patient Details
                          </span>
                          <span className="subHeaderTitle">
                            Basic information
                          </span>
                        </Col>
                      </Row>
                    </h2>

                    <div
                      className={`accordion-collapse collapse show ${
                        isCardVisible ? "visible" : ""
                      }`}
                    >
                      <div className="accordion-body pb-4">
                        {/* Order & Patient Fields */}
                        <Row className="pt-2">
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="ERF Type"
                              data={erfTypeOptions}
                              name="erfType"
                              textField="name"
                              setValue={formInputs.erfType}
                              getvalue={setDropdownData}
                              required={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              key={`privilege-${formInputs.erfType}`}
                              label="Privilege Case"
                              data={privilegeOptions}
                              name="privilegeCase"
                              textField="name"
                              setValue={formInputs.privilegeCase}
                              getvalue={setDropdownData}
                              disabled={formInputs.erfType === "Select"}
                              required={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              type="date"
                              label="ERF Order Date"
                              name="orderDate"
                              value={formInputs.orderDate}
                              onChange={handleInputChange}
                              // disabled={!adminUser}
                              disabled={!isFormEnabled}
                              error={validation.expectedDeliveryDate}
                              min={new Date().toISOString().split("T")[0]}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="Sale Point"
                              data={SALE_POINT_OPTIONS}
                              name="salePoint"
                              textField="name"
                              setValue={formInputs.salePoint}
                              getvalue={setDropdownData}
                              disabled={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="Company"
                              data={companyList}
                              name="selectedCompanyId"
                              textField="name"
                              setValue={selectedCompany?.name || ""}
                              getvalue={setDropdownData}
                              disabled={!isFormEnabled}
                              placeholder={"Select Company"}
                              required={true}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="ERF Order Type"
                              data={ORDER_TYPE_OPTIONS}
                              name="orderType"
                              textField="name"
                              setValue={formInputs.orderType}
                              getvalue={setDropdownData}
                              disabled={!isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="Source of Call"
                              data={SOURCE_OF_CALL_OPTIONS}
                              name="sourceOfCall"
                              textField="name"
                              setValue={formInputs.sourceOfCall}
                              getvalue={setDropdownData}
                              disabled={!isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            {/* <InputField
                              className="inputBox"
                              label="Cost Center"
                              name="costCenter"
                              value={formInputs.costCenter}
                              onChange={handleInputChange}
                              disabled={true}
                            /> */}
                            {isPCDUser ? (
                              // PCD users get a searchable dropdown from the API list
                              <ComboboxField
                                label="Cost Center"
                                data={costCenterList}
                                name="costCenter"
                                textField="name"
                                setValue={formInputs.costCenter}
                                getvalue={setDropdownData}
                                disabled={!isFormEnabled || isLoadingCostCenter}
                                required={true}
                                placeholder={
                                  isLoadingCostCenter
                                    ? "Loading..."
                                    : "Select Sales Rep"
                                }
                              />
                            ) : (
                              // All other roles see their own name, read-only
                              <InputField
                                className="inputBox"
                                label="Cost Center"
                                name="costCenter"
                                value={formInputs.costCenter}
                                disabled={true}
                              />
                            )}
                            {/* <ComboboxField
                              label="Cost Center"
                              data={COST_POINT_OPTIONS}
                              name="costCenter"
                              textField="name"
                              setValue={formInputs.costCenter}
                              getvalue={setDropdownData}
                              disabled={!isPCDUser || !isFormEnabled} // only PCD can change it
                              required={true}
                              placeholder="Select Sales Rep"
                            /> */}
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Billing Name"
                              name="billingName"
                              value={formInputs.billingName}
                              onChange={handleInputChange}
                              disabled={!isFormEnabled}
                            />
                          </Col>
                        </Row>
                        <Row>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              type="date"
                              label="Expected Delivery Date"
                              name="expectedDeliveryDate"
                              value={formInputs.expectedDeliveryDate}
                              onChange={handleInputChange}
                              disabled={!isFormEnabled}
                              required={true}
                              error={validation.expectedDeliveryDate}
                              min={new Date().toISOString().split("T")[0]}
                            />
                          </Col>

                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              type="time"
                              label="Expected Delivery Time (24hr)"
                              name="expectedDeliveryTime"
                              value={formInputs.expectedDeliveryTime}
                              onChange={handleInputChange}
                              disabled={!isFormEnabled}
                              required={true}
                            />
                          </Col>
                        </Row>

                        {/* ERF For Type Selection */}
                        <Row className="pt-2 mb-4 bg-light p-3 rounded">
                          <Col xs={12}>
                            <h6 className="text-primary mb-3">
                              ERF Type Selection
                            </h6>
                            <div className="d-flex gap-4">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="erfForType"
                                  id="erfForPatient"
                                  value="Patient"
                                  checked={erfForType === "Patient"}
                                  onChange={(e) => {
                                    setErfForType(e.target.value);
                                    setSelectedHospital(null);
                                    setRentItems([]);
                                    setSaleItems([]);
                                    setNursingItems([]);
                                    setPriceViolations([]);
                                    setShowApprovalRequired(false);
                                    setFormInputs((prev) => ({
                                      ...prev,
                                      costCenter: userData.firstName || "",
                                      erfCostCenterSubGroup: "SALESTEAM",
                                      approvers: [],
                                      //--Clear hospital fields
                                      hospitalName: "",
                                      hospitalAssociateNumber: "",
                                      hospitalMobileNr: "",
                                      hospitalEmail: "",
                                      hospitalGstNo: "",
                                      hospitalAddress: "",
                                      hospitalAddress2: "",
                                      hospitalCity: "",
                                      hospitalState: "",
                                      hospitalPinCode: "",
                                      hospitalBillingAddress: "",
                                      //--Clear security fields
                                      receivedSecurityAmt: "",
                                      securityCashAmt: "",
                                      securityModeCash: "",
                                      securityModeBal: 0,
                                      securityMode: "Select",
                                      securityAmtMode: "Select",
                                    }));
                                  }}
                                  disabled={!isFormEnabled}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="erfForPatient"
                                >
                                  <strong>Patient</strong>
                                </label>
                              </div>
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="erfForType"
                                  id="erfForHospital"
                                  value="Hospital"
                                  checked={erfForType === "Hospital"}
                                  onChange={(e) => {
                                    setErfForType(e.target.value);
                                    setRentItems([]);
                                    setSaleItems([]);
                                    setNursingItems([]);
                                    setPriceViolations([]);
                                    setShowApprovalRequired(false);
                                    setFormInputs((prev) => ({
                                      ...prev,
                                      erfCostCenterSubGroup: "HOSPITAL",
                                      approvers: [],
                                      //--Clear security fields (hospital has no security)
                                      receivedSecurityAmt: "",
                                      securityCashAmt: "",
                                      securityModeCash: "",
                                      securityModeBal: 0,
                                      securityMode: "Select",
                                      securityAmtMode: "Select",
                                      securityAmtModeCode: "",
                                      securityAmtModeName: "",
                                      securityModeCode: "",
                                      securityModeName: "",
                                    }));
                                  }}
                                  disabled={!isFormEnabled}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="erfForHospital"
                                >
                                  <strong>Hospital</strong>
                                </label>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </div>
                </div>

                {/* HOSPITAL INFORMATION SECTION - Only show for B2B */}
                {erfForType === "Hospital" && (
                  <div className="accordion mb-4" id="accordionHospital">
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <Row className="accordion-button">
                          <Col
                            xs={1}
                            md={1}
                            lg={1}
                            className="text-left arrowBtn"
                          >
                            <i className="bi bi-chevron-down"></i>
                          </Col>
                          <Col
                            xs={1}
                            md={1}
                            lg={1}
                            className="text-center logoBtn"
                          >
                            <span className="iconLogo">HI</span>
                          </Col>
                          <Col xs={10} md={6} lg={8}>
                            <span className="headerTitle">
                              Hospital Information
                            </span>
                          </Col>
                        </Row>
                      </h2>

                      <div className="accordion-collapse collapse show visible">
                        <div className="accordion-body pb-4">
                          {/* Hospital Selection */}
                          <Row className="mb-4">
                            <Col
                              xs={12}
                              md={6}
                              lg={4}
                              className="commTopButtonRightLeftPadding"
                            >
                              <ComboboxField
                                key={`erfHospital-${erfForType}`}
                                label="Select Hospital For ERF"
                                data={erfHospitalList}
                                name="selectedHospitalId"
                                textField="name"
                                // setValue={selectedHospital?.userId || ""}
                                setValue={
                                  selectedHospital
                                    ? selectedHospital.hospitalName ||
                                      selectedHospital.organisationName
                                    : ""
                                }
                                getvalue={setDropdownData}
                                disabled={!isFormEnabled}
                                isLoading={isErfLoadingHospitals}
                                placeholder="Select Hospital"
                                required={true}
                              />
                            </Col>
                          </Row>

                          {/* Hospital Details Display (Read-Only) */}
                          <>
                            <Row>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Hospital Name"
                                  name="hospitalName"
                                  value={formInputs.hospitalName}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Association Number"
                                  name="hospitalAssociateNumber"
                                  value={formInputs.hospitalAssociateNumber}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Contact Number"
                                  name="hospitalMobileNr"
                                  value={formInputs.hospitalMobileNr}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Hospital Email"
                                  name="hospitalEmail"
                                  value={formInputs.hospitalEmail}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                            </Row>

                            <Row>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="GST No"
                                  name="hospitalGstNo"
                                  value={formInputs.hospitalGstNo}
                                  onChange={handleInputChange}
                                  // disabled={disabled || !isFormEnabled}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Address Line 1"
                                  name="hospitalAddress"
                                  value={formInputs.hospitalAddress}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Address Line 2"
                                  name="hospitalAddress2"
                                  value={formInputs.hospitalAddress2}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="City"
                                  name="hospitalCity"
                                  value={formInputs.hospitalCity}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={6}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <TextAreaField
                                  className="inputBox"
                                  label="Billing Address"
                                  name="hospitalBillingAddress"
                                  value={formInputs.hospitalBillingAddress}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="State"
                                  name="hospitalState"
                                  value={formInputs.hospitalState}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Postal Code"
                                  name="hospitalPinCode"
                                  value={formInputs.hospitalPinCode}
                                  onChange={handleInputChange}
                                  disabled={true}
                                />
                              </Col>
                            </Row>
                          </>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* PATIENT INFORMATION  */}
                <div className="accordion mb-4" id="accordionExample">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <Row className="accordion-button">
                        <Col
                          xs={1}
                          md={1}
                          lg={1}
                          className="text-left arrowBtn"
                        >
                          <i
                            className={`bi ${
                              isCardVisible3
                                ? "bi-chevron-down"
                                : "bi-chevron-right"
                            }`}
                            onClick={toggleCardVisibility3}
                          ></i>
                        </Col>
                        <Col
                          xs={1}
                          md={1}
                          lg={1}
                          className="text-center logoBtn"
                        >
                          <span className="iconLogo">PI</span>
                        </Col>
                        <Col xs={10}>
                          <span className="headerTitle">
                            Patient Information
                          </span>
                        </Col>
                      </Row>
                    </h2>

                    <div
                      className={`accordion-collapse collapse show ${
                        isCardVisible3 ? "visible" : ""
                      }`}
                    >
                      <div className="accordion-body pb-4">
                        {!isHospital && (
                          <Row className="pt-3">
                            {/* Doctor Info */}
                            <h6
                              className="text-primary mb-3 ps-2"
                              style={{ fontSize: "14px", fontWeight: "600" }}
                            >
                              Doctor Information
                            </h6>
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <ComboboxField
                                label="Dr. Type"
                                data={DOCTOR_TYPE_OPTIONS}
                                name="drType"
                                textField="name"
                                setValue={formInputs.drType}
                                getvalue={setDropdownData}
                                disabled={!isFormEnabled}
                                // required
                              />
                            </Col>

                            {formInputs.drType === "Listed" && (
                              <>
                                <Col
                                  xs={12}
                                  md={6}
                                  lg={3}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <ComboboxField
                                    label="Search By"
                                    data={DOCTOR_TYPE_SELECTION_OPTIONS}
                                    name="drListedType"
                                    textField="name"
                                    setValue={formInputs.drListedType || ""}
                                    getvalue={setDropdownData}
                                    disabled={disabled || !isFormEnabled}
                                    required
                                  />
                                </Col>

                                {formInputs.drListedType === "Hospital" && (
                                  <Col
                                    xs={12}
                                    md={6}
                                    lg={3}
                                    className="commTopButtonRightLeftPadding mb-3"
                                  >
                                    <ComboboxField
                                      label="Select Hospital"
                                      data={hospitalList}
                                      name="patientErfHospitalName"
                                      textField="name"
                                      setValue={
                                        formInputs.patientErfHospitalName || ""
                                      }
                                      getvalue={setDropdownData}
                                      disabled={disabled || !isFormEnabled}
                                      isLoading={isLoadingHospitals}
                                      placeholder="Select Hospital"
                                      required
                                    />
                                  </Col>
                                )}

                                {(formInputs.drListedType === "Doctor" ||
                                  (formInputs.drListedType === "Hospital" &&
                                    formInputs.patientErfHospitalName)) && (
                                  <Col
                                    xs={12}
                                    md={6}
                                    lg={3}
                                    className="commTopButtonRightLeftPadding mb-3"
                                  >
                                    <ComboboxField
                                      label="Select Doctor"
                                      data={doctorsList}
                                      name="doctorName"
                                      textField="name"
                                      setValue={formInputs.doctorName || ""}
                                      getvalue={setDropdownData}
                                      disabled={
                                        disabled ||
                                        !isFormEnabled ||
                                        isLoadingDoctors
                                      }
                                      isLoading={isLoadingDoctors}
                                      placeholder={
                                        isLoadingDoctors
                                          ? "Loading doctors..."
                                          : "Select Doctor"
                                      }
                                      required
                                    />
                                  </Col>
                                )}

                                {formInputs.doctorName && (
                                  <Col
                                    xs={12}
                                    md={6}
                                    lg={3}
                                    className="commTopButtonRightLeftPadding mb-3"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Doctor Name"
                                      name="doctorName"
                                      value={formInputs.doctorName || ""}
                                      disabled={true}
                                    />
                                  </Col>
                                )}

                                {formInputs.doctorName && (
                                  <Col
                                    xs={12}
                                    md={6}
                                    lg={3}
                                    className="commTopButtonRightLeftPadding mb-3"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Dr. Code"
                                      name="drCode"
                                      value={formInputs.drCode || ""}
                                      disabled={true}
                                    />
                                  </Col>
                                )}
                              </>
                            )}

                            {formInputs.drType === "Non-Listed" && (
                              <>
                                <Col
                                  xs={12}
                                  md={6}
                                  lg={3}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <InputField
                                    className="inputBox"
                                    label="Doctor Name"
                                    name="doctorName"
                                    value={formInputs.doctorName || ""}
                                    onChange={handleInputChange}
                                    disabled={disabled || !isFormEnabled}
                                    placeholder="Enter doctor name"
                                  />
                                </Col>

                                <Col
                                  xs={12}
                                  md={6}
                                  lg={3}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <InputField
                                    className="inputBox"
                                    label="Dr. Code"
                                    name="drCode"
                                    value={formInputs.drCode || ""}
                                    onChange={handleInputChange}
                                    disabled={disabled || !isFormEnabled}
                                    placeholder="Enter doctor code"
                                  />
                                </Col>
                              </>
                            )}

                            {formInputs.drType === "Not Given" && (
                              <>
                                <Col
                                  xs={12}
                                  md={6}
                                  lg={3}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <InputField
                                    className="inputBox"
                                    label="Doctor Name"
                                    name="doctorName"
                                    value=""
                                    disabled={true}
                                    placeholder="Not applicable"
                                  />
                                </Col>

                                <Col
                                  xs={12}
                                  md={6}
                                  lg={3}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <InputField
                                    className="inputBox"
                                    label="Dr. Code"
                                    name="drCode"
                                    value=""
                                    disabled={true}
                                    placeholder="Not applicable"
                                  />
                                </Col>
                              </>
                            )}
                          </Row>
                        )}

                        {/* ERF Autocomplete Search */}
                        {/* <Row className="mb-4">
                          <Col xs={12}>
                            <div className="p-3 bg-light rounded border">
                              <h6 className="text-primary mb-3">
                                Quick Fill from previous ERF
                              </h6>

                              <ERFAutocompleteSearch
                                onERFSelect={handleERFSelection}
                                disabled={!isFormEnabled}
                                placeholder="Type patient name or ERF number (min. 2 characters)..."
                              />

                              {isERFLoaded && selectedERF && (
                                <div
                                  className="alert alert-soft-primary d-flex align-items-center mt-3 border-0 shadow-sm"
                                  role="alert"
                                >
                                  <div className="fs-4 me-3 text-primary">
                                    <i className="bi bi-check-circle-fill"></i>
                                  </div>
                                  <div className="flex-grow-1">
                                    <h6 className="alert-heading fw-bold mb-1">
                                      ERF Loaded Successfully
                                    </h6>
                                    <p className="mb-0 small text-muted">
                                      <strong>ID:</strong>{" "}
                                      {selectedERF.documentNumber} &bull;
                                      <strong> Patient:</strong>{" "}
                                      {selectedERF.patientName} &bull;
                                      <strong> Contact:</strong>{" "}
                                      {selectedERF.mobileNr}
                                    </p>
                                  </div>
                                  <button
                                    className="btn btn-sm btn-outline-danger ms-3"
                                    onClick={() => {
                                      setSelectedERF(null);
                                      setIsERFLoaded(false);
                                    }}
                                  >
                                    <i className="bi bi-x-lg me-1"></i> Clear
                                  </button>
                                </div>
                              )}
                            </div>
                          </Col>
                        </Row> */}
                        {!isHospital && <hr className="my-3 text-muted" />}

                        {/* Patient Info */}
                        <h6
                          className="text-primary mb-3 ps-2"
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                          }}
                        >
                          Patient Information
                        </h6>
                        <Row>
                          <Row>
                            <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <ComboboxField
                                label="Patient Type"
                                data={PATIENT_TYPE_OPTIONS}
                                name="patientType"
                                textField="name"
                                setValue={formInputs.patientType}
                                getvalue={setDropdownData}
                                disabled={!isFormEnabled}
                              />
                            </Col>

                            {formInputs.patientType === "Listed" && (
                              <>
                                <Col
                                  xs={12}
                                  md={6}
                                  lg={3}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <ComboboxField
                                    key={`patient-${formInputs.patientType}`}
                                    label="Select Patient"
                                    data={patientList}
                                    name="patient"
                                    textField="name"
                                    setValue={formInputs.patient}
                                    getvalue={setDropdownData}
                                    disabled={!isFormEnabled}
                                    isLoading={isLoadingPatient}
                                    placeholder="Select Patient"
                                  />
                                </Col>
                                <Col
                                  xs={12}
                                  md={6}
                                  lg={3}
                                  className="commTopButtonRightLeftPadding mb-3"
                                >
                                  <InputField
                                    className="inputBox"
                                    label="Association Number"
                                    disabled={true}
                                    name="AssociateNumber"
                                    value={formInputs.AssociateNumber}
                                    onChange={handleInputChange}
                                  />
                                </Col>
                              </>
                            )}
                          </Row>

                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Patient Name"
                              name="patientName"
                              value={formInputs.patientName}
                              onChange={handleInputChange}
                              required
                              error={validation.patientName}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Contact No"
                              name="mobileNr"
                              value={formInputs.mobileNr}
                              onChange={handleInputChange}
                              required
                              type="number"
                              error={validation.mobileNr}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Email"
                              name="email"
                              value={formInputs.email}
                              onChange={handleInputChange}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Aadhaar No"
                              name="aadharNo"
                              value={formInputs.aadharNo}
                              onChange={handleInputChange}
                              // required
                              error={validation.aadharNo}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col
                            xs={12}
                            md={4}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Address Line 1"
                              name="address"
                              value={formInputs.address}
                              onChange={handleInputChange}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Address Line 2"
                              name="address2"
                              value={formInputs.address2}
                              onChange={handleInputChange}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="City"
                              name="city"
                              value={formInputs.city}
                              onChange={handleInputChange}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>

                          <Col
                            xs={6}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Age"
                              name="age"
                              value={formInputs.age}
                              onChange={handleInputChange}
                              type="number"
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={6}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Weight (Kg)"
                              name="weight"
                              value={formInputs.weight}
                              onChange={handleInputChange}
                              type="number"
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="Gender"
                              data={GENDER_OPTIONS}
                              name="gender"
                              textField="name"
                              setValue={formInputs.gender}
                              getvalue={setDropdownData}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="Is Diabetic?"
                              data={IS_DIABETIC_OPTIONS}
                              name="isDiabetic"
                              textField="name"
                              setValue={formInputs.isDiabetic}
                              getvalue={setDropdownData}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          {/* <Col
                              xs={12}
                              md={6}
                              lg={3}
                              className="commTopButtonRightLeftPadding mb-3"
                            >
                              <InputField
                                className="inputBox"
                                label="GST No"
                                name="gstNo"
                                value={formInputs.gstNo}
                                onChange={handleInputChange}
                                disabled={disabled || !isFormEnabled}
                              />
                            </Col> */}
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="PAN No"
                              name="panNo"
                              value={formInputs.panNo}
                              onChange={handleInputChange}
                              disabled={disabled || !isFormEnabled}
                              error={validation.panNo}
                            />
                          </Col>

                          <Col
                            xs={12}
                            md={12}
                            lg={12}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <TextAreaField
                              className="inputBox"
                              label="Patient Challenges"
                              name="patientChallenges"
                              value={formInputs.patientChallenges}
                              onChange={handleInputChange}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                        </Row>

                        <hr className="my-3 text-muted" />

                        {/* Caregiver Info */}
                        <h6
                          className="text-primary mb-3 ps-2"
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                          }}
                        >
                          Caregiver Information
                        </h6>
                        <Row>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Caregiver Name"
                              name="caregiverName"
                              value={formInputs.caregiverName}
                              onChange={handleInputChange}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Caregiver Contact No"
                              name="caregiverMobileNr"
                              value={formInputs.caregiverMobileNr}
                              onChange={handleInputChange}
                              required={true}
                              type="number"
                              error={validation.caregiverMobileNr}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={3}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Email ID"
                              name="emailId"
                              value={formInputs.emailId}
                              onChange={handleInputChange}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={2}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <InputField
                              className="inputBox"
                              label="Relationship"
                              name="caregiverRelation"
                              value={formInputs.caregiverRelation}
                              onChange={handleInputChange}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          <Col
                            xs={12}
                            md={6}
                            lg={2}
                            className="commTopButtonRightLeftPadding mb-3"
                          >
                            <ComboboxField
                              label="ID Proof Type"
                              data={ID_PROOF_OPTIONS}
                              name="idProof"
                              textField="name"
                              setValue={formInputs.idProof}
                              getvalue={setDropdownData}
                              disabled={disabled || !isFormEnabled}
                            />
                          </Col>
                          {formInputs.idProof &&
                            formInputs.idProof !== "Select" && (
                              <Col
                                xs={12}
                                md={6}
                                lg={2}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label={`${formInputs.idProof} Number`}
                                  name="idProofNumber"
                                  placeholder={`Enter ${formInputs.idProof} number`}
                                  value={formInputs.idProofNumber}
                                  onChange={handleInputChange}
                                  disabled={disabled || !isFormEnabled}
                                  // required
                                  error={validation.idProofNumber}
                                />
                              </Col>
                            )}
                        </Row>

                        {/* Addresses */}
                        <Row className="mt-2">
                          <Col md={6} className="pe-md-4">
                            <Row>
                              <h6
                                className="text-primary mb-3 ps-2"
                                style={{
                                  fontSize: "14px",
                                  fontWeight: "600",
                                }}
                              >
                                Billing Address
                              </h6>
                            </Row>
                            <Row>
                              <Col
                                xs={12}
                                md={12}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <TextAreaField
                                  className="inputBox"
                                  label="Address"
                                  name="billingAddress"
                                  value={formInputs.billingAddress}
                                  onChange={handleInputChange}
                                  disabled={disabled || !isFormEnabled}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <ComboboxField
                                  label="State"
                                  data={STATE_OPTIONS}
                                  name="billingState"
                                  textField="name"
                                  setValue={formInputs.billingState}
                                  getvalue={setDropdownData}
                                  disabled={disabled || !isFormEnabled}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Pin Code"
                                  name="billingPinCode"
                                  value={formInputs.billingPinCode}
                                  onChange={handleInputChange}
                                  type="number"
                                  disabled={disabled || !isFormEnabled}
                                />
                              </Col>
                            </Row>
                          </Col>
                          <Col md={6} className="ps-md-4 border-start-md">
                            <Row>
                              <Col md={3} lg={3}>
                                <h6
                                  className="text-primary mb-3 ps-2"
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                  }}
                                >
                                  Delivery Address
                                </h6>
                              </Col>
                              <Col md={3} lg={3}>
                                {/* Same as Billing Checkbox */}
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="sameAsBilling"
                                    checked={isSameAddress}
                                    onChange={(e) => {
                                      setIsSameAddress(e.target.checked);
                                      if (e.target.checked) {
                                        // Copy billing to delivery
                                        setFormInputs((prev) => ({
                                          ...prev,
                                          deliveryAddress: prev.billingAddress,
                                          deliveryState: prev.billingState,
                                          deliveryPinCode: prev.billingPinCode,
                                        }));
                                      }
                                    }}
                                    disabled={!isFormEnabled}
                                  />
                                  <label
                                    className="form-check-label small"
                                    htmlFor="sameAsBilling"
                                  >
                                    Same as Billing Address
                                  </label>
                                </div>
                              </Col>
                            </Row>
                            <Row>
                              <Col
                                xs={12}
                                md={12}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <TextAreaField
                                  className="inputBox"
                                  label="Address"
                                  name="deliveryAddress"
                                  value={formInputs.deliveryAddress}
                                  onChange={handleInputChange}
                                  disabled={disabled || !isFormEnabled}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <ComboboxField
                                  label="State"
                                  data={STATE_OPTIONS}
                                  name="deliveryState"
                                  textField="name"
                                  setValue={formInputs.deliveryState}
                                  getvalue={setDropdownData}
                                  disabled={disabled || !isFormEnabled}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Pin Code"
                                  name="deliveryPinCode"
                                  value={formInputs.deliveryPinCode}
                                  onChange={handleInputChange}
                                  type="number"
                                  disabled={disabled || !isFormEnabled}
                                />
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </div>
                </div>

                {formInputs.erfType === "Nursing Service" && (
                  <div className="accordion mb-4" id="accordionExample">
                    <div className="accordion-item">
                      <h2 className="accordion-header">
                        <Row className="accordion-button">
                          <Col
                            xs={1}
                            md={1}
                            lg={1}
                            className="text-left arrowBtn"
                          >
                            <i className="bi bi-chevron-down"></i>
                          </Col>
                          <Col
                            xs={1}
                            md={1}
                            lg={1}
                            className="text-center logoBtn"
                          >
                            <span className="iconLogo">NI</span>
                          </Col>
                          <Col xs={10} md={6} lg={8}>
                            <span className="headerTitle">
                              Nurse Information
                            </span>
                          </Col>
                        </Row>
                      </h2>

                      <div className="accordion-collapse collapse show visible">
                        <div className="accordion-body pb-4">
                          {/* Hospital Selection */}
                          <Row className="mb-4">
                            <Col
                              xs={12}
                              md={6}
                              lg={4}
                              className="commTopButtonRightLeftPadding"
                            >
                              <ComboboxField
                                key={`erfNurse-${erfForType}`}
                                label="Select Nurse For ERF"
                                data={erfNurseList}
                                name="selectedNurseId"
                                textField="name"
                                // setValue={selectedHospital?.userId || ""}
                                setValue={
                                  selectedNurse
                                    ? selectedNurse.hospitalName ||
                                      selectedNurse.organisationName
                                    : ""
                                }
                                getvalue={setDropdownData}
                                disabled={!isFormEnabled}
                                isLoading={isLoadingErfNurse}
                                placeholder="Select Nurse"
                                required={true}
                              />
                            </Col>
                          </Row>

                          {/* Nurse Details Display (Read-Only) */}
                          <>
                            <Row>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Nurse Name"
                                  name="nurseName"
                                  value={formInputs.nurseName}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Association Number"
                                  name="nurseAssociateNumber"
                                  value={formInputs.nurseAssociateNumber}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Contact Number"
                                  name="nurseMobileNr"
                                  value={formInputs.nurseMobileNr}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Email"
                                  name="nurseEmail"
                                  value={formInputs.nurseEmail}
                                  disabled={true}
                                />
                              </Col>
                            </Row>
                            <Row>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Pan No"
                                  name="nursePanNo"
                                  value={formInputs.nursePanNo}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Address Line 1"
                                  name="nurseAddress"
                                  value={formInputs.nurseAddress}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Address Line 2"
                                  name="nurseAddress2"
                                  value={formInputs.nurseAddress2}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="City"
                                  name="nurseCity"
                                  value={formInputs.nurseCity}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Service Provider Code"
                                  name="serviceProviderCode"
                                  value={formInputs.serviceProviderCode}
                                  disabled={true}
                                />
                              </Col>
                              <Col
                                xs={12}
                                md={6}
                                lg={3}
                                className="commTopButtonRightLeftPadding mb-3"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Service Provider Name"
                                  name="serviceProviderName"
                                  value={formInputs.serviceProviderName}
                                  disabled={true}
                                />
                              </Col>
                            </Row>
                          </>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* GLOBAL RENT CONFIGURATION - Add this BEFORE Product Details Accordion */}
                {(formInputs.erfType === "Rent" ||
                  formInputs.erfType === "Sale & Rent" ||
                  formInputs.erfType === "Nursing Service") && (
                  <div className="mb-2">
                    <div className="p-2 border rounded bg-white">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="text-primary mb-0">
                          Rent Period Configuration
                        </h6>
                      </div>
                      <GlobalRentConfigPanel
                        config={globalRentConfig}
                        onChange={setGlobalRentConfig}
                        disabled={!isFormEnabled}
                        // itemCount={rentItems.length}
                        itemCount={
                          formInputs.erfType === "Nursing Service"
                            ? nursingItems.length
                            : rentItems.length
                        }
                      />
                    </div>
                  </div>
                )}

                {/* PRICE SCHEDULE (PRODUCT DETAILS) ACCORDION */}
                <div className="accordion mb-4" id="accordionProduct">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <Row className="accordion-button">
                        <Col
                          xs={1}
                          md={1}
                          lg={1}
                          className="text-left arrowBtn"
                        >
                          <i
                            className={`bi ${
                              isCardVisible2
                                ? "bi-chevron-down"
                                : "bi-chevron-right"
                            }`}
                            onClick={toggleCardVisibility2}
                          ></i>
                        </Col>
                        <Col
                          xs={1}
                          md={1}
                          lg={1}
                          className="text-center logoBtn"
                        >
                          <span className="iconLogo">P</span>
                        </Col>
                        <Col xs={10} md={6} lg={8}>
                          <span className="headerTitle">Product Details</span>
                        </Col>
                      </Row>
                    </h2>
                    <div
                      className={`accordion-collapse collapse show ${
                        isCardVisible2 ? "visible" : ""
                      }`}
                    >
                      <div className="accordion-body">
                        {/* RENT TABLE */}
                        {(formInputs.erfType === "Rent" ||
                          formInputs.erfType === "Sale & Rent") && (
                          <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center bg-light p-2 border rounded-top">
                              <Col
                                xs={8}
                                md={8}
                                lg={10}
                                className="commTopButtonRightLeftPadding"
                              >
                                <h6 className="fw-bold mb-0 text-primary ps-2">
                                  Rent Products
                                </h6>
                              </Col>
                              <Col
                                xs={4}
                                md={4}
                                lg={2}
                                className="commTopButtonRightLeftPadding"
                              >
                                <button
                                  className="btnTable"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addProductDetails("Rent");
                                  }}
                                  disabled={!isFormEnabled}
                                >
                                  <i className="bi bi-plus-lg"></i> Add Rent
                                  Item
                                </button>
                              </Col>
                            </div>
                            <DataTable
                              // columns={getColumns("Rent")}
                              columns={rentColumns}
                              data={getDataWithTotal(rentItems)}
                              pagination
                              paginationRowsPerPageOptions={
                                Constants.ROW_PER_PAGE
                              }
                              noDataComponent={
                                <div className="p-2 text-center text-muted">
                                  No Rent items.
                                </div>
                              }
                              conditionalRowStyles={CONDITIONAL_ROW_STYLES}
                            />
                            <hr />
                          </div>
                        )}

                        {/* SALE / SERVICE TABLE */}
                        {(formInputs.erfType === "Sale" ||
                          formInputs.erfType === "Sale & Rent") && (
                          <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center bg-light p-2 border rounded-top">
                              <Col
                                xs={8}
                                md={8}
                                lg={10}
                                className="commTopButtonRightLeftPadding"
                              >
                                <h6 className="fw-bold mb-0 text-primary ps-2">
                                  Sale Products
                                </h6>
                              </Col>
                              <Col
                                xs={4}
                                md={4}
                                lg={2}
                                className="commTopButtonRightLeftPadding"
                              >
                                <button
                                  className="btnTable"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addProductDetails(
                                      formInputs.erfType === "Sale & Rent"
                                        ? "Sale"
                                        : "Sale",
                                    );
                                  }}
                                  disabled={!isFormEnabled}
                                >
                                  <i className="bi bi-plus-lg"></i> Add Sale
                                  Item
                                </button>
                              </Col>
                            </div>
                            <DataTable
                              columns={saleColumns}
                              data={getDataWithTotal(saleItems)}
                              pagination
                              paginationRowsPerPageOptions={
                                Constants.ROW_PER_PAGE
                              }
                              noDataComponent={
                                <div className="p-2 text-center text-muted">
                                  No Sale items.
                                </div>
                              }
                              conditionalRowStyles={CONDITIONAL_ROW_STYLES}
                            />
                            <hr />
                          </div>
                        )}

                        {/* SERVICE TABLE (with barcode) - NEW SEPARATE BLOCK */}
                        {formInputs.erfType === "Service" && (
                          <div className="mb-4">
                            <div className="d-flex justify-content-between align-items-center bg-light p-2 border rounded-top">
                              <Col
                                xs={8}
                                md={8}
                                lg={10}
                                className="commTopButtonRightLeftPadding"
                              >
                                <h6 className="fw-bold mb-0 text-primary ps-2">
                                  Service Items
                                </h6>
                              </Col>
                              <Col
                                xs={4}
                                md={4}
                                lg={2}
                                className="commTopButtonRightLeftPadding"
                              >
                                <button
                                  className="btnTable"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addProductDetails("Service");
                                  }}
                                  disabled={!isFormEnabled}
                                >
                                  <i className="bi bi-plus-lg"></i> Add Service
                                  Item
                                </button>
                              </Col>
                            </div>
                            <DataTable
                              columns={serviceColumns}
                              data={getDataWithTotal(saleItems)}
                              pagination
                              paginationRowsPerPageOptions={
                                Constants.ROW_PER_PAGE
                              }
                              noDataComponent={
                                <div className="p-2 text-center text-muted">
                                  No Service items.
                                </div>
                              }
                              conditionalRowStyles={CONDITIONAL_ROW_STYLES}
                            />
                            <hr />
                          </div>
                        )}

                        {/* ── NURSING SERVICE TABLE ────────────────────────── */}
                        {formInputs.erfType === "Nursing Service" && (
                          <div className="mb-4">
                            <div
                              className="d-flex justify-content-between align-items-center bg-light p-2 border rounded-top"
                              style={{ borderLeft: "4px solid #0d6efd" }}
                            >
                              <Col
                                xs={8}
                                md={8}
                                lg={10}
                                className="commTopButtonRightLeftPadding"
                              >
                                <h6 className="fw-bold mb-0 text-primary ps-2">
                                  Nursing Service Items
                                </h6>
                              </Col>
                              <Col
                                xs={4}
                                md={4}
                                lg={2}
                                className="commTopButtonRightLeftPadding"
                              >
                                <button
                                  className="btnTable"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    addProductDetails("Nursing");
                                  }}
                                  disabled={!isFormEnabled}
                                >
                                  <i className="bi bi-plus-lg"></i> Add Nursing
                                  Item
                                </button>
                              </Col>
                            </div>
                            <DataTable
                              columns={nursingColumns}
                              data={getDataWithTotalNursing(nursingItems)}
                              pagination
                              paginationRowsPerPageOptions={
                                Constants.ROW_PER_PAGE
                              }
                              noDataComponent={
                                <div className="p-2 text-center text-muted">
                                  No Nursing Service items.
                                </div>
                              }
                              conditionalRowStyles={CONDITIONAL_ROW_STYLES}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* PAYMENT DETAILS */}
                <div className="accordion mb-4" id="accordionPayment">
                  <div className="accordion-item">
                    <h2 className="accordion-header">
                      <Row className="accordion-button">
                        <Col
                          xs={1}
                          md={1}
                          lg={1}
                          className="text-left arrowBtn"
                        >
                          <i
                            className={`bi ${visible.PAY ? "bi-chevron-down" : "bi-chevron-right"}`}
                            onClick={() => toggleVisibility("PAY")}
                          ></i>
                        </Col>
                        <Col
                          xs={1}
                          md={1}
                          lg={1}
                          className="text-center logoBtn"
                        >
                          <span className="iconLogo">PD</span>
                        </Col>
                        <Col xs={10} md={6} lg={8}>
                          <span className="headerTitle">Payment Details</span>
                        </Col>
                      </Row>
                    </h2>
                    <div
                      className={`accordion-collapse collapse show ${visible.PAY ? "visible" : ""}`}
                    >
                      <div className="accordion-body">
                        <Row>
                          <Col md={6} className="pe-md-4">
                            <h6 className="text-primary mb-3 mt-3 ps-2">
                              Payment Details
                            </h6>
                            <Row className="mb-3">
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Total Amount"
                                  value={totalPayable}
                                  disabled
                                />
                              </Col>
                              {(formInputs?.erfTypeCode === "SL" ||
                                formInputs?.erfTypeCode === "RE" ||
                                formInputs?.erfTypeCode === "SLRE") && (
                                <Col
                                  md={12}
                                  className="commTopButtonRightLeftPadding"
                                >
                                  <InputField
                                    className="inputBox"
                                    label="Cartage"
                                    name="cartage"
                                    onChange={handleInputChange}
                                    type="number"
                                    disabled={
                                      !isFormEnabled ||
                                      (
                                        PRIVILEGE_SETTINGS[
                                          formInputs.privilegeCase
                                        ] || DEFAULT_PRIVILEGE_SETTINGS
                                      ).disableCartage
                                    }
                                  />

                                  {/* <Row>
                                    <Col
                                      md={4}
                                      className="commTopButtonRightLeftPadding"
                                    >
                                      <label>
                                        <input
                                          type="checkbox"
                                          name="salesCartage"
                                          checked={cartageCheck.salesCartage}
                                          onChange={handleCartageCheck}
                                        />
                                        Sales Cartage
                                      </label>
                                    </Col>

                                    <Col
                                      md={4}
                                      className="commTopButtonRightLeftPadding"
                                    >
                                      <label>
                                        <input
                                          type="checkbox"
                                          name="rentCartage"
                                          checked={cartageCheck.rentCartage}
                                          onChange={handleCartageCheck}
                                        />
                                        Rent Cartage
                                      </label>
                                    </Col>
                                  </Row> */}
                                  <Row>
                                    <Col
                                      md={4}
                                      className="commTopButtonRightLeftPadding"
                                    >
                                      <label
                                        style={{
                                          cursor:
                                            formInputs.erfTypeCode === "SLRE"
                                              ? "pointer"
                                              : "not-allowed",
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          name="salesCartage"
                                          checked={cartageCheck.salesCartage}
                                          onChange={handleCartageCheck}
                                          // Only SLRE lets the user pick; SL/RE are auto-set and locked
                                          disabled={
                                            formInputs.erfTypeCode !== "SLRE"
                                          }
                                        />
                                        Sales
                                      </label>
                                    </Col>

                                    <Col
                                      md={4}
                                      className="commTopButtonRightLeftPadding"
                                    >
                                      <label
                                        style={{
                                          cursor:
                                            formInputs.erfTypeCode === "SLRE"
                                              ? "pointer"
                                              : "not-allowed",
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          name="rentCartage"
                                          checked={cartageCheck.rentCartage}
                                          onChange={handleCartageCheck}
                                          disabled={
                                            formInputs.erfTypeCode !== "SLRE"
                                          }
                                        />
                                        Rent
                                      </label>
                                    </Col>
                                  </Row>
                                </Col>
                              )}
                              {(formInputs?.erfTypeCode === "SL" ||
                                formInputs?.erfTypeCode === "RE" ||
                                formInputs?.erfTypeCode === "SLRE" ||
                                formInputs?.erfTypeCode === "SLRE") && (
                                <Col
                                  md={12}
                                  className="commTopButtonRightLeftPadding"
                                >
                                  <InputField
                                    className="inputBox"
                                    label="Installation Charges"
                                    name="installationCharges"
                                    onChange={handleInputChange}
                                    type="number"
                                    disabled={!isFormEnabled}
                                  />
                                </Col>
                              )}
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Net Amount"
                                  value={netAmount}
                                  disabled
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Enter Received Amount"
                                  name="receivedAmount"
                                  value={formInputs.receivedAmount}
                                  onChange={handleInputChange}
                                  type="number"
                                  disabled={
                                    !isFormEnabled ||
                                    !isPaymentModeSelected ||
                                    formInputs.paymentMode === "NR"
                                  }
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Balance Amount"
                                  value={balanceAmount}
                                  disabled
                                />
                              </Col>
                            </Row>
                          </Col>
                          <Col md={6} className="ps-md-4 border-start-md">
                            <h6 className="text-primary mb-3 mt-3 ps-2">
                              Received Amount Breakup
                            </h6>
                            <Row className="mb-3">
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <ComboboxField
                                  key={`paymentMode-${formInputs.erfType}-${formInputs.privilegeCase}`}
                                  label="Payment Mode"
                                  data={paymentModeDropdown}
                                  name="paymentMode"
                                  textField="name"
                                  setValue={formInputs.paymentMode}
                                  getvalue={setDropdownData}
                                  disabled={!isFormEnabled}
                                  required={true}
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Cash"
                                  name="cashAmount"
                                  onChange={handleInputChange}
                                  type="number"
                                  disabled={
                                    !isFormEnabled ||
                                    !isPaymentModeSelected || //   lock until mode selected
                                    paymentRules.disableCash ||
                                    paymentRules.disableAll
                                  }
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <Row>
                                  <Col md={4}>
                                    <label className="mt-2">
                                      {/* Dynamic Label (Req 3) */}
                                      {formInputs.paymentMode === "Card&Cash"
                                        ? "Card"
                                        : formInputs.paymentMode ===
                                            "Cheque&Cash"
                                          ? "Cheque"
                                          : formInputs.paymentMode ===
                                              "Card&Cheque"
                                            ? "Cheque"
                                            : "Bal. Amt"}
                                    </label>
                                  </Col>
                                  <Col md={8}>
                                    <InputField
                                      className="inputBox"
                                      name="balanceAmtBreakup"
                                      value={formInputs.balanceAmtBreakup}
                                      disabled
                                    />
                                  </Col>
                                </Row>
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Cheque No."
                                  name="chequeNo"
                                  onChange={handleInputChange}
                                  disabled={
                                    !isFormEnabled ||
                                    !isPaymentModeSelected ||
                                    paymentRules.disableCheque ||
                                    paymentRules.disableAll
                                  }
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Card Trans. No."
                                  name="cardTransNo"
                                  onChange={handleInputChange}
                                  disabled={
                                    !isFormEnabled ||
                                    !isPaymentModeSelected ||
                                    paymentRules.disableCard ||
                                    paymentRules.disableAll
                                  }
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Bank Name"
                                  name="bankName"
                                  onChange={handleInputChange}
                                  disabled={
                                    !isFormEnabled ||
                                    !isPaymentModeSelected ||
                                    paymentRules.disableBank ||
                                    paymentRules.disableAll
                                  }
                                />
                              </Col>
                              <Col
                                md={12}
                                className="commTopButtonRightLeftPadding"
                              >
                                <InputField
                                  className="inputBox"
                                  label="Receipt No."
                                  name="receiptNo"
                                  onChange={handleInputChange}
                                  disabled={
                                    !isFormEnabled ||
                                    !isPaymentModeSelected ||
                                    paymentRules.disableReceipt ||
                                    paymentRules.disableAll
                                  }
                                />
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECURITY DETAILS */}
                {(formInputs.erfType === "Rent" ||
                  formInputs.erfType === "Sale & Rent") &&
                  !isHospital && (
                    <div className="accordion mb-4" id="accordionSecurity">
                      <div className="accordion-item">
                        <h2 className="accordion-header">
                          <Row className="accordion-button">
                            <Col
                              xs={1}
                              md={1}
                              lg={1}
                              className="text-left arrowBtn"
                            >
                              <i
                                className={`bi ${visible.SEC ? "bi-chevron-down" : "bi-chevron-right"}`}
                                onClick={() => toggleVisibility("SEC")}
                              ></i>
                            </Col>
                            <Col
                              xs={1}
                              md={1}
                              lg={1}
                              className="text-center logoBtn"
                            >
                              <span className="iconLogo">SD</span>
                            </Col>
                            <Col xs={10} md={6} lg={8}>
                              <span className="headerTitle">
                                Security Details
                              </span>
                            </Col>
                          </Row>
                        </h2>
                        <div
                          className={`accordion-collapse collapse show ${visible.SEC ? "visible" : ""}`}
                        >
                          <div className="accordion-body">
                            <Row>
                              <Col md={6} className="pe-md-4">
                                <Row className="mb-3 pt-3">
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Standard Security Amount"
                                      // value={formInputs.standardSecurityAmt}
                                      value={totals.standardSecurityAmt.toFixed(
                                        2,
                                      )}
                                      disabled
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Total Security Amount*"
                                      value={totals.security.toFixed(2)}
                                      disabled={true}
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Enter Received Security Amount"
                                      name="receivedSecurityAmt"
                                      onChange={handleInputChange}
                                      type="number"
                                      disabled={
                                        !isFormEnabled ||
                                        !isSecurityAmtModeSelected || //   lock until mode selected
                                        formInputs.securityAmtMode === "NR" ||
                                        privilegeSettings.disableSecurityDetails
                                      }
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Balance Security Amount"
                                      value={balanceSecurityAmt}
                                      disabled
                                    />
                                  </Col>
                                </Row>
                              </Col>
                              <Col md={6} className="ps-md-4 border-start-md">
                                <Row className="mb-3 pt-3">
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Enter Pincode"
                                      name="securityPincode"
                                      onChange={handleInputChange}
                                      disabled={!isFormEnabled}
                                      required={true}
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <ComboboxField
                                      key={`securityAmtMode-${formInputs.erfType}-${formInputs.privilegeCase}`}
                                      label="Security Amt. Mode"
                                      data={(() => {
                                        // Filter options based on Privilege Case settings
                                        const settings =
                                          PRIVILEGE_SETTINGS[
                                            formInputs.privilegeCase
                                          ] || DEFAULT_PRIVILEGE_SETTINGS;
                                        if (settings.securityAmtModeOptions) {
                                          return paymentModeDropdown.filter(
                                            (opt) =>
                                              settings.securityAmtModeOptions.includes(
                                                opt.name,
                                              ),
                                          );
                                        }
                                        return paymentModeDropdown;
                                      })()}
                                      name="securityAmtMode"
                                      textField="name"
                                      setValue={formInputs.securityAmtMode}
                                      getvalue={setDropdownData}
                                      required={true}
                                      disabled={
                                        !isFormEnabled ||
                                        privilegeSettings.disableSecurityDetails ||
                                        !formInputs.securityPincode //  Still requires pincode first
                                      }
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Cash Amount Received"
                                      name="securityCashAmt"
                                      onChange={handleInputChange}
                                      type="number"
                                      disabled={
                                        !isFormEnabled ||
                                        !formInputs.securityPincode ||
                                        !isSecurityAmtModeSelected ||
                                        privilegeSettings.disableSecurityDetails ||
                                        securityAmtRules.disableCash ||
                                        securityAmtRules.disableAll
                                      }
                                    />
                                  </Col>

                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <Row>
                                      <Col md={4}>
                                        <label className="mt-2">
                                          {formInputs.securityAmtMode ===
                                          "Card&Cash"
                                            ? "Card"
                                            : formInputs.securityAmtMode ===
                                                "Cheque&Cash"
                                              ? "Cheque"
                                              : formInputs.securityAmtMode ===
                                                  "Card&Cheque"
                                                ? "Cheque"
                                                : "Bal. Amt"}
                                        </label>
                                      </Col>
                                      <Col md={8}>
                                        <InputField
                                          className="inputBox"
                                          name="balanceSecurityBreakup"
                                          value={
                                            formInputs.balanceSecurityBreakup
                                          }
                                          disabled
                                        />
                                      </Col>
                                    </Row>
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Cheque No."
                                      name="securityChequeNo"
                                      onChange={handleInputChange}
                                      disabled={
                                        !isFormEnabled ||
                                        !formInputs.securityPincode ||
                                        !isSecurityAmtModeSelected ||
                                        privilegeSettings.disableSecurityDetails ||
                                        securityAmtRules.disableCheque ||
                                        securityAmtRules.disableAll
                                      }
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Card Trans. No."
                                      name="securityCardTransNo"
                                      onChange={handleInputChange}
                                      disabled={
                                        !isFormEnabled ||
                                        !formInputs.securityPincode ||
                                        !isSecurityAmtModeSelected ||
                                        privilegeSettings.disableSecurityDetails ||
                                        securityAmtRules.disableCard ||
                                        securityAmtRules.disableAll
                                      }
                                    />
                                  </Col>
                                  <Col
                                    md={12}
                                    className="commTopButtonRightLeftPadding"
                                  >
                                    <InputField
                                      className="inputBox"
                                      label="Bank Name"
                                      name="securityBankName"
                                      onChange={handleInputChange}
                                      disabled={
                                        !isFormEnabled ||
                                        !formInputs.securityPincode ||
                                        !isSecurityAmtModeSelected ||
                                        privilegeSettings.disableSecurityDetails ||
                                        securityAmtRules.disableBank ||
                                        securityAmtRules.disableAll
                                      }
                                    />
                                  </Col>
                                </Row>
                              </Col>
                            </Row>

                            {/* Security Mode Would be (Req 4) */}
                            {(formInputs.erfType === "Rent" ||
                              formInputs.erfType === "Sale & Rent") && (
                              <div
                                className="accordion mb-4"
                                id="accordionSecurityMode"
                              >
                                <div className="accordion-item">
                                  <div className="accordion-body p-4">
                                    <h6 className="text-primary mb-3">
                                      Security Mode Would be
                                    </h6>
                                    <Row className="align-items-center">
                                      <Col
                                        md={3}
                                        className="commTopButtonRightLeftPadding"
                                      >
                                        <ComboboxField
                                          key={`securityMode-${formInputs.erfType}-${formInputs.privilegeCase}`}
                                          label="Security Mode"
                                          data={paymentModeDropdown.filter(
                                            (m) => m.name !== "NR",
                                          )}
                                          name="securityMode"
                                          textField="name"
                                          setValue={formInputs.securityMode}
                                          getvalue={setDropdownData}
                                          disabled={!isFormEnabled}
                                          required={true}
                                        />
                                      </Col>
                                      <Col
                                        md={3}
                                        className="commTopButtonRightLeftPadding"
                                      >
                                        <InputField
                                          className="inputBox"
                                          label="Cash Amount"
                                          name="securityModeCash"
                                          onChange={handleInputChange}
                                          type="number"
                                          disabled={
                                            !isFormEnabled ||
                                            ![
                                              "Card&Cash",
                                              "Cheque&Cash",
                                            ].includes(formInputs.securityMode)
                                          }
                                        />
                                      </Col>
                                      <Col
                                        md={3}
                                        className="commTopButtonRightLeftPadding"
                                      >
                                        <InputField
                                          className="inputBox"
                                          label="Balance Security Amount"
                                          value={(() => {
                                            // Point 4, 25, 26, 27
                                            if (
                                              [
                                                "Cash",
                                                "Euro",
                                                "Dollar",
                                              ].includes(
                                                formInputs.securityMode,
                                              )
                                            ) {
                                              return 0;
                                            }
                                            if (
                                              [
                                                "Card",
                                                "Cheque",
                                                "Online",
                                                "Paytm",
                                                "UPI",
                                                "Card&Cheque",
                                              ].includes(
                                                formInputs.securityMode,
                                              )
                                            ) {
                                              return balanceSecurityAmt;
                                            }
                                            // Else (Card&Cash, Cheque&Cash) - calculated via useEffect/State, displayed here
                                            return formInputs.securityModeBal;
                                          })()}
                                          disabled
                                        />
                                      </Col>
                                    </Row>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                {/* Remarks */}
                <div className="mb-4 p-4 border rounded bg-white">
                  <TextAreaField
                    className="inputBox"
                    label="Remarks"
                    name="remarks"
                    value={formInputs.remarks}
                    onChange={handleInputChange}
                    disabled={!isFormEnabled}
                  />
                </div>

                {/* PRICE VIOLATION APPROVAL SECTION */}
                {showApprovalRequired && priceViolations.length > 0 && (
                  <div className="accordion mb-4" id="accordionPriceViolation">
                    <div className="accordion-item border-warning">
                      <h2 className="accordion-header">
                        <Row className="accordion-button bg-warning bg-opacity-10">
                          <Col xs={10}>
                            <span className="headerTitle text-dark">
                              Price Violations Detected
                            </span>
                            <span className="subHeaderTitle text-muted d-block">
                              Please review and either correct prices or select
                              an approver
                            </span>
                          </Col>
                          <Col xs={1} className="text-end">
                            {/* {formInputs.approvers[0]?.userId && (
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => {
                                  // Acknowledge violations
                                  // const violatedItemIds = new Set(
                                  //   priceViolations.map((v) => v.itemId),
                                  // );
                                  // setAcknowledgedViolations(violatedItemIds);
                                  setShowApprovalRequired(false);
                                }}
                                title="Acknowledge violations (approver already selected)"
                              >
                                <i className="bi bi-x-lg"></i>
                              </button>
                            )} */}
                          </Col>
                        </Row>
                      </h2>

                      <div className="accordion-collapse collapse show visible">
                        <div className="accordion-body pb-4 bg-light">
                          {/* Violations Summary */}
                          {/* <div className="alert alert-warning mb-3 d-flex align-items-start">
                            <i className="bi bi-info-circle-fill me-3 fs-5"></i>
                            <div className="flex-grow-1">
                              <h6 className="fw-bold mb-2">Action Required:</h6>
                              <ul className="mb-0 ps-3">
                                <li>
                                  Select an approver
                                  below to proceed with current prices
                                </li>
                              </ul>
                            </div>
                          </div> */}

                          {/* Approver Selection */}
                          <Row className="align-items-end">
                            <Col
                              xs={12}
                              md={6}
                              lg={4}
                              className="commTopButtonRightLeftPadding"
                            >
                              <ComboboxField
                                key={`violation-approver-${formInputs.erfType}-${formInputs.privilegeCase}-${formInputs.approvers[0]?.userId || "empty"}`}
                                label="Select Approver (Required for Price Violations)"
                                data={APPROVED_BY_OPTIONS_LEVEL_ONE}
                                name="violation_approver"
                                textField="name"
                                required={true}
                                getvalue={handleApproverFirstIndexSelection}
                                setValue={formInputs.approvers[0]?.name || ""}
                                disabled={!isFormEnabled}
                              />
                            </Col>
                            <Col
                              xs={12}
                              md={6}
                              lg={4}
                              className="commTopButtonRightLeftPadding"
                            >
                              {formInputs.approvers[0]?.userId ? (
                                <div className="alert alert-success mb-0 py-2">
                                  <i className="bi bi-check-circle-fill me-2"></i>
                                  <strong>Approver Selected:</strong>{" "}
                                  {formInputs.approvers[0].name}
                                </div>
                              ) : (
                                <div className="alert alert-danger mb-0 py-2">
                                  <i className="bi bi-x-circle-fill me-2"></i>
                                  <strong>No approver selected yet</strong>
                                </div>
                              )}
                            </Col>
                          </Row>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* FOOTER */}
                <div className="pageFooter mb-4">
                  <Row className="justify-content-end">
                    <Col xs={6} md={2} className="pb-2">
                      <button
                        className="btnTable btn-danger text-white w-100"
                        onClick={() => navigate(-1)}
                        disabled={disabled}
                      >
                        Cancel
                      </button>
                    </Col>
                    <Col xs={6} md={2} className="pb-2 ps-2">
                      <button
                        className="btnTable w-100"
                        onClick={handleSave}
                        disabled={!isFormEnabled || disabled}
                      >
                        <i className="bi bi-save"></i> Create
                      </button>
                    </Col>
                  </Row>
                </div>
              </>
            )}
          </div>
        </Col>
      </Row>

      {/* <MaterialSelectionModalV5
        show={modalShow}
        onHide={hideModal}
        onSelectedData={handleGetSelectedMaterial}
        itemType={activeItemType}
      /> */}
      <MaterialSelectionModalV5
        show={modalShow}
        onHide={hideModal}
        onSelectedData={handleGetSelectedMaterial}
        itemType={activeItemType}
        itemTypeCode={activeItemTypeCode}
        selectedHospitalCode={selectedHospital?.AssociateNumber || ""}
        selectedHospitalName={selectedHospital?.hospitalName || ""}
        erfForType={erfForType || "Patient"}
      />

      <FailureModal
        show={getShowFailureModal}
        onHide={hideFailureModal}
        failuremodaldata={failureModalData}
        requiredfields={getErrorFields}
      />
    </main>
  );
};

export default CreateERF;
